/**
 * reviewLogic — pure, UI-free logic for the access-reviews redesign (PLO-4818).
 *
 * Every stateful decision the redesign makes lives here as a plain function so
 * it can be unit-tested without rendering (the repo's test setup runs in a
 * `node` environment with no jsdom/RTL — see vitest.config.ts). The React
 * components import these helpers and stay presentational.
 */

import type {
  MFCampaign,
  MFCampaignReview,
  MFDeadlineExtension,
  MFAccessReviewAccountEntitlement,
  MFGroupedPersonRow,
  MFPersonIdentity,
  MFReviewAccount,
  MFReviewDetails,
  MFUndecidedRowsRouting,
} from "state/members/accessReviews";
import {
  accessKindOf,
  hopLabel,
  isIndirectAccount,
  type MFAccessKind,
  type MFAccessPath,
} from "state/accessPaths";
import type { MFMember } from "state/members/member";

/* ------------------------------------------------------------------ *
 * Progress + completion
 * ------------------------------------------------------------------ */

/** Clamp a done/total pair to a 0–100 integer-safe percentage. */
export const progressPercent = (done: number, total: number): number => {
  if (total <= 0) return 0;
  const pct = (done / total) * 100;
  return Math.min(100, Math.max(0, pct));
};

/** A meter is "complete" only when everything is reviewed AND there is work. */
export const isComplete = (done: number, total: number): boolean =>
  total > 0 && done >= total;

/** Display name priority for single-account decision copy. */
export const reviewAccountDisplayName = (account: MFReviewAccount): string => {
  const memberName = account.member.name?.trim();
  if (memberName) return memberName;

  const identityName = account.identity?.displayName?.trim();
  if (identityName) return identityName;

  if (isNhiAccount(account))
    return account.identity?.extId?.trim() || "Unnamed";
  return account.member.email?.trim() || "Unnamed";
};

/**
 * A non-human identity row. NHI rows arrive with `member.id == null` and
 * either no member name or the Postgres `concat(NULL, ' ', NULL)` whitespace
 * artefact, or carry a non-human identity type.
 */
export const isNhiAccount = (account: MFReviewAccount): boolean =>
  !account.member.id ||
  (account.identity?.type !== undefined && account.identity.type !== "human");

/**
 * The name the review table's Account cell shows. An NHI leads with its
 * identity name; a person leads with their member name. The table sorts on
 * this, so the order matches what the reviewer reads (PLO-6218).
 */
export const accountCellName = (account: MFReviewAccount): string =>
  isNhiAccount(account)
    ? account.identity?.displayName ||
      account.identity?.extId ||
      account.member.name?.trim() ||
      "Unnamed"
    : account.member.name?.trim() || account.member.email || "Unnamed";

export type NameSortDirection = "asc" | "desc";

const nameCollator = new Intl.Collator(undefined, {
  sensitivity: "base",
  numeric: true,
});

// Who a row belongs to: two people can share a display name.
const rowOwnerKey = (account: MFReviewAccount): string =>
  account.member.id ?? account.identity?.extId ?? account.id;

/**
 * Order review rows by their Account cell name (PLO-6218). The server returns
 * them in account-id order, which scatters one person's rows. Rows with the
 * same name are grouped by owner, and the sort is stable, so each person's
 * rows (e.g. their entitlements in an entitlement-only cycle) form one block
 * in load order in both directions.
 */
export const sortRowsByName = <T extends MFReviewAccount>(
  rows: readonly T[],
  direction: NameSortDirection,
): T[] => {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const byName = nameCollator.compare(accountCellName(a), accountCellName(b));
    if (byName !== 0) return sign * byName;
    const ownerA = rowOwnerKey(a);
    const ownerB = rowOwnerKey(b);
    return ownerA < ownerB ? -1 : ownerA > ownerB ? 1 : 0;
  });
};

/* ------------------------------------------------------------------ *
 * Due dates + overdue derivation
 *
 * "Overdue" is NOT a status — it's derived from the due date on a still-open
 * cycle and surfaced in the meta description with a red blip, per the design's
 * data.ts note.
 * ------------------------------------------------------------------ */

export type DueUrgency = "overdue" | "soon" | "normal";

/** Whole days from `now` until `dueDate` (negative = past). */
export const daysUntil = (
  dueDate: string | Date,
  now: Date = new Date(),
): number => {
  const due = new Date(dueDate);
  const diffMs = due.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Due-date urgency for a campaign. A *completed* campaign is never overdue —
 * overdue only applies while the cycle is still open.
 */
export const dueUrgency = (
  dueDate: string | Date,
  status: MFCampaign["status"],
  now: Date = new Date(),
): DueUrgency => {
  if (status === "completed") return "normal";
  const days = daysUntil(dueDate, now);
  if (days < 0) return "overdue";
  if (days <= 3) return "soon";
  return "normal";
};

/**
 * Whether the review page reads "Overdue by Nd": only while the review's cycle
 * is still open. Once the admin has ended it there is nothing left to be late
 * with (PLO-6551).
 */
export const reviewIsOverdue = (
  effectiveDueDays: number | null,
  cycleStatus: MFReviewDetails["cycleStatus"],
): boolean =>
  effectiveDueDays != null &&
  effectiveDueDays < 0 &&
  !isCycleEnded(cycleStatus);

/* ------------------------------------------------------------------ *
 * Campaigns page — Open / Completed tabs
 * ------------------------------------------------------------------ */

export type CampaignTab = "open" | "completed";

/**
 * Which tab a campaign belongs to *from the reviewer's side* (PLO-5326).
 *
 * `MFCampaign.status` is cycle-wide: it stays `in_progress` until the attestor
 * approves, so keying the tab on it alone filed a reviewer's finished campaign
 * under Open — while the card's own pill said "Awaiting admin approval". The
 * admin-approval stage is not the reviewer's work, so it belongs in Completed.
 *
 * Derived from `campaignStatusPillKind` rather than re-deciding it here, so the
 * tab and the pill can never disagree again.
 *
 * `reviewsComplete` is deliberately REQUIRED, not defaulted to `false`: a
 * default would let a future call site omit it and silently file a finished
 * campaign back under Open — the exact bug this fixes, reintroduced with no
 * type error. It must already encode the total > 0 guard (see `isComplete`).
 */
export const campaignTabOf = (
  c: Pick<MFCampaign, "status">,
  reviewsComplete: boolean,
): CampaignTab => {
  const kind = campaignStatusPillKind(c.status, reviewsComplete);
  return kind === "complete" || kind === "awaiting" ? "completed" : "open";
};

/**
 * Split a flat campaign list into the two tabs.
 *
 * `reviewsCompleteById` carries each campaign's reviewer-side progress (see
 * `campaignTabOf`) and is required for the same reason. A campaign missing from
 * the map — details still loading — is treated as Open, so a half-loaded list
 * never flashes rows into Completed; pass `{}` to mean "nothing resolved yet".
 */
export const bucketCampaignsByTab = <
  T extends Pick<MFCampaign, "id" | "status">,
>(
  campaigns: T[],
  reviewsCompleteById: Record<string, boolean>,
): Record<CampaignTab, T[]> => {
  const open: T[] = [];
  const completed: T[] = [];
  for (const c of campaigns) {
    if (campaignTabOf(c, reviewsCompleteById[c.id] ?? false) === "completed")
      completed.push(c);
    else open.push(c);
  }
  return { open, completed };
};

/* ------------------------------------------------------------------ *
 * Resource drawer — group reviews by status
 *
 * Groups render in a fixed order; completed resources are faded (disabled).
 * Revision-flagged reviews float to the top of their group.
 * ------------------------------------------------------------------ */

export type ReviewGroupKey = "in_progress" | "ready_for_approval" | "complete";

export interface ReviewGroup {
  key: ReviewGroupKey;
  label: string;
  reviews: MFCampaignReview[];
  /** completed resources render faded + non-actionable */
  faded: boolean;
}

const REVIEW_GROUP_ORDER: {
  key: ReviewGroupKey;
  label: string;
  faded: boolean;
}[] = [
  { key: "in_progress", label: "In progress", faded: false },
  { key: "ready_for_approval", label: "Awaiting approval", faded: false },
  { key: "complete", label: "Complete", faded: true },
];

/** revision-flagged first, then by name — stable within a group. */
const sortWithinGroup = (a: MFCampaignReview, b: MFCampaignReview): number => {
  const ar = a.hasRevisionRequested ? 1 : 0;
  const br = b.hasRevisionRequested ? 1 : 0;
  if (ar !== br) return br - ar;
  return (a.resource?.name ?? "").localeCompare(b.resource?.name ?? "");
};

export const groupReviewsByStatus = (
  reviews: MFCampaignReview[],
): ReviewGroup[] =>
  REVIEW_GROUP_ORDER.map(({ key, label, faded }) => ({
    key,
    label,
    faded,
    reviews: reviews.filter((r) => r.status === key).sort(sortWithinGroup),
  })).filter((g) => g.reviews.length > 0);

/**
 * The reviewer's resource-list group-by mode, round-tripped through the URL
 * (`review_group_by`). "status" renders the status groups above; "none" (the
 * absent default) renders one flat list in the server's sort order. Only these
 * two are valid — the members API declares the same `none | status` set.
 */
export type ReviewGroupBy = "none" | "status";

/** Read the group-by mode from a raw URL value; anything but "status" = "none". */
export const parseReviewGroupBy = (
  raw: string | null | undefined,
): ReviewGroupBy => (raw === "status" ? "status" : "none");

/** The grouped-by-status shape the reviews payload sometimes arrives in. */
type GroupedReviews = {
  in_progress?: MFCampaignReview[];
  inProgress?: MFCampaignReview[];
  ready_for_approval?: MFCampaignReview[];
  complete?: MFCampaignReview[];
};

/**
 * Count how many of a campaign's resource reviews have a revision requested,
 * accepting either the flat array or the grouped payload shape. Drives the
 * revision-warning chip on the campaign card + details header (PLO-4803) — the
 * redesign dropped that signal.
 */
export const revisionRequestedCount = (
  reviews: MFCampaignReview[] | GroupedReviews | null | undefined,
): number => {
  if (!reviews) return 0;
  const flat = Array.isArray(reviews)
    ? reviews
    : [
        ...(reviews.in_progress ?? reviews.inProgress ?? []),
        ...(reviews.ready_for_approval ?? []),
        ...(reviews.complete ?? []),
      ];
  return flat.filter((r) => r.hasRevisionRequested).length;
};

/* ------------------------------------------------------------------ *
 * Per-application instructions (PLO-4817)
 *
 * Admins can scope a free-text instruction to one application (resource) of a
 * campaign; reviewers see it while reviewing that resource's accounts. The
 * review-details payload carries the single matching text
 * (`review.applicationInstruction`); the campaign payload carries the full
 * list (`campaign.applicationInstructions`) for the resource drawer.
 * ------------------------------------------------------------------ */

/**
 * Normalise an instruction for display: trimmed text, or null when there is
 * nothing renderable (missing field, empty, or whitespace-only). The
 * Instructions callout renders iff this returns non-null.
 */
export const applicationInstructionText = (
  instruction: string | null | undefined,
): string | null => {
  const trimmed = instruction?.trim();
  return trimmed ? trimmed : null;
};

/**
 * The instruction for a specific resource from a campaign's instruction list,
 * normalised through `applicationInstructionText`. Null when the list is
 * missing or has no entry for the resource.
 */
export const instructionForResource = (
  instructions: MFCampaign["applicationInstructions"],
  resourceId: string | null | undefined,
): string | null => {
  if (!resourceId) return null;
  const match = (instructions ?? []).find((i) => i.resourceId === resourceId);
  return applicationInstructionText(match?.text);
};

/* ------------------------------------------------------------------ *
 * Multi-stage reviewer workflow (PLO-4806)
 *
 * A later review stage re-reviews access that an earlier reviewer KEPT. The
 * SME sees the review with a "2nd review" badge and, per row, the stage-1
 * decision context (who kept it, when, and their note).
 * ------------------------------------------------------------------ */

/**
 * Whether this reviewer's set is a later (2nd+) review stage. Prefers the
 * explicit `isSecondReview` flag, falling back to `accountSetStage > 1`. Older
 * API responses (no stage fields) are stage 1 → false.
 */
export const isSecondReview = (
  details: Pick<MFReviewDetails, "isSecondReview" | "accountSetStage">,
): boolean =>
  details.isSecondReview === true || (details.accountSetStage ?? 1) > 1;

/**
 * The badge label for a review's stage, or null when it's the implicit first
 * stage (no badge). Stage 2 reads "2nd review"; higher stages read "Nth
 * review".
 */
export const stageBadgeLabel = (
  stage: number | null | undefined,
): string | null => {
  const n = stage ?? 1;
  if (n <= 1) return null;
  if (n === 2) return "2nd review";
  if (n === 3) return "3rd review";
  return `${n}th review`;
};

export interface Stage1Context {
  review?: string | null;
  note?: string | null;
  reviewedAt?: string | null;
  reviewedByName?: string | null;
}

/** Human-readable phrase for a stage-1 outcome (`required` → "kept access"). */
export const stage1OutcomeLabel = (
  review: string | null | undefined,
): string => {
  switch (review) {
    case "required":
    case "compliant":
    case "appropriate":
      return "kept access";
    case "adjust_entitlements":
      return "flagged for adjustment";
    default:
      return "reviewed";
  }
};

/**
 * A compact one-line hint summarising the stage-1 decision for a 2nd-review
 * row, e.g. "Kept by Alex Lee on 12 Jun". Null when there is no stage-1
 * context (a stage-1 row, or missing data).
 */
export const stage1ContextHint = (
  ctx: Stage1Context | null | undefined,
  now: Date = new Date(),
): string | null => {
  if (!ctx || !ctx.review) return null;

  const who = ctx.reviewedByName?.trim();
  const outcome = stage1OutcomeLabel(ctx.review);
  const when = formatStageDate(ctx.reviewedAt, now);

  const verb = outcome.charAt(0).toUpperCase() + outcome.slice(1);
  const byPart = who ? ` by ${who}` : "";
  const onPart = when ? ` on ${when}` : "";

  return `${verb}${byPart}${onPart}`;
};

/** Whether a row carries any stage-1 context worth surfacing. */
export const hasStage1Context = (
  ctx: Stage1Context | null | undefined,
): boolean => !!ctx && !!ctx.review;

/**
 * The structured "Previous review" block for a 2nd+-stage row: the prior
 * outcome phrase, who decided it, when, and the stage it came from. Null when
 * the row carries no prior-stage context (a stage-1 row / single-pass review),
 * so a single-stage review renders nothing extra.
 */
export interface PreviousReview {
  /** e.g. "Kept access" / "Flagged for adjustment" / "Reviewed". */
  outcomeLabel: string;
  /** the raw prior review value (for tone/icon decisions). */
  outcome: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
  /** the stage the prior decision was made at, e.g. "Stage 1". */
  stageLabel: string;
  note: string | null;
}

/** The stage the previous decision was made at — one below this row's stage. */
export const previousStageLabel = (
  stage: number | null | undefined,
): string => {
  const n = stage ?? 2;
  const prior = n > 1 ? n - 1 : 1;
  return `Stage ${prior}`;
};

/**
 * The "Previous review" column header, generalised to the actual prior stage —
 * "Stage 1 review" on a 2nd-stage review, "Stage 2 review" on a 3rd-stage one,
 * etc. Only ever called when the column is shown (stage > 1), so the default
 * (no stage supplied) reads "Stage 1 review".
 */
export const previousReviewColumnLabel = (
  stage: number | null | undefined,
): string => `${previousStageLabel(stage)} review`;

/**
 * Map a row's stage-1 context fields into a structured PreviousReview, or null
 * when there's nothing to show. Drives the per-row "Previous review" block on a
 * re-review list.
 */
export const previousReviewOf = (
  account: Pick<
    MFReviewAccount,
    | "stage"
    | "stage1Review"
    | "stage1Note"
    | "stage1ReviewedAt"
    | "stage1ReviewedByName"
  >,
): PreviousReview | null => {
  const outcome = account.stage1Review;
  if (!outcome) return null;
  return {
    outcome,
    outcomeLabel: capitalise(stage1OutcomeLabel(outcome)),
    reviewedByName: account.stage1ReviewedByName?.trim() || null,
    reviewedAt: account.stage1ReviewedAt ?? null,
    stageLabel: previousStageLabel(account.stage),
    note: account.stage1Note?.trim() || null,
  };
};

const capitalise = (s: string): string =>
  s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * In a multi-stage review the SAME member can be the reviewer at more than one
 * stage, so their list carries earlier-stage rows (all already decided) mixed in
 * with the ONE current stage they're actually re-reviewing. The main table
 * should show only the CURRENT stage — the highest stage present among the
 * member's loaded rows — and fold EVERY lower-stage row (whether or not it has a
 * current-stage successor) into a read-only "Previously reviewed" section.
 *
 * A single-stage review (max stage 1, i.e. no re-review) is a pass-through:
 * every row is active and nothing folds, so it renders exactly as before.
 * Order is preserved within each bucket.
 */
export interface StageRowPartition<T> {
  /** rows to render as actionable — the current (highest) stage only. */
  active: T[];
  /** all lower-stage rows, decided at an earlier stage, folded away. */
  previouslyReviewed: T[];
}

const stageOf = (account: Pick<MFReviewAccount, "stage">): number =>
  account.stage ?? 1;

/** The current (highest) review stage present among the loaded rows; 1 when
 *  there are no rows or nothing carries a stage (a single-pass review). */
export const currentStage = <T extends Pick<MFReviewAccount, "stage">>(
  accounts: T[],
): number => accounts.reduce((max, a) => Math.max(max, stageOf(a)), 1);

/** Whether the loaded rows represent a multi-stage (re-review) context. */
export const isMultiStageContext = <T extends Pick<MFReviewAccount, "stage">>(
  accounts: T[],
): boolean => currentStage(accounts) > 1;

export const partitionStageRows = <T extends Pick<MFReviewAccount, "stage">>(
  accounts: T[],
): StageRowPartition<T> => {
  const max = currentStage(accounts);
  // Single-stage review — pass everything through untouched.
  if (max <= 1) return { active: accounts, previouslyReviewed: [] };

  const active: T[] = [];
  const previouslyReviewed: T[] = [];
  for (const a of accounts) {
    if (stageOf(a) === max) active.push(a);
    else previouslyReviewed.push(a);
  }
  return { active, previouslyReviewed };
};

/** Whether any row folds into the "Previously reviewed" section. */
export const hasPreviouslyReviewed = <T extends Pick<MFReviewAccount, "stage">>(
  accounts: T[],
): boolean => partitionStageRows(accounts).previouslyReviewed.length > 0;

export interface StageCounts {
  total: number;
  completed: number;
}

/**
 * The completion counts for the CURRENT-stage rows only — what the header ring /
 * "X of Y reviewed" / "N left" should read in a re-review, so the chrome agrees
 * with the folded list (the earlier-stage decided rows don't inflate it). Client
 * derived from the loaded rows; use it only in a multi-stage context (a
 * single-stage review keeps the server-fed stats, which aren't page-limited).
 */
export const currentStageCounts = <
  T extends Pick<MFReviewAccount, "stage" | "review">,
>(
  accounts: T[],
): StageCounts => {
  const { active } = partitionStageRows(accounts);
  return {
    total: active.length,
    completed: active.filter((a) => isReviewed(a.review)).length,
  };
};

/**
 * The stage badge for a re-review, e.g. "Stage 2 of 3". Null on the implicit
 * first stage (no badge). `stageCount` (N) falls back to the stage itself when
 * the payload omits it, so it reads "Stage 2 of 2" rather than "of 1".
 */
export const reReviewBadge = (
  stage: number | null | undefined,
  stageCount: number | null | undefined,
): string | null => {
  const n = stage ?? 1;
  if (n <= 1) return null;
  const total = Math.max(stageCount ?? n, n);
  return `Stage ${n} of ${total}`;
};

const ORDINAL_WORDS = [
  "zeroth",
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
];

/** The reviewer's ordinal position for a stage — "second" at stage 2, etc. */
export const reviewerOrdinal = (stage: number): string =>
  ORDINAL_WORDS[stage] ?? `${stage}th`;

/**
 * One-sentence re-review context, e.g. "You are the second reviewer of these
 * accounts. Access shown here was kept at stage 1." Null on the first stage.
 */
export const reReviewSentence = (
  stage: number | null | undefined,
): string | null => {
  const n = stage ?? 1;
  if (n <= 1) return null;
  return `You are the ${reviewerOrdinal(n)} reviewer of these accounts. Access shown here was kept at stage ${n - 1}.`;
};

/** A short outcome word for the Previous-review tooltip (accessible label for
 *  the icon). */
export const previousOutcomeShort = (
  outcome: string | null | undefined,
): string => {
  switch (outcome) {
    case "required":
    case "compliant":
    case "appropriate":
      return "Kept";
    case "adjust_entitlements":
      return "Adjusted";
    default:
      return "Reviewed";
  }
};

/**
 * Which Actions-column icon vocabulary a prior outcome reads as, so the
 * Previous-review cell shows the SAME green-check / red-X / orange-flag /
 * grey-minus glyphs the decision buttons use. Pure so the mapping is tested
 * without rendering; the cell maps the kind to the shared icon component + tone.
 */
export type PreviousOutcomeIconKind = "kept" | "removal" | "adjust" | "neutral";

export const previousOutcomeIconKind = (
  outcome: string | null | undefined,
): PreviousOutcomeIconKind => {
  switch (outcome) {
    case "required":
    case "compliant":
    case "appropriate":
    case "keep":
      return "kept";
    case "removed":
    case "remove":
      return "removal";
    case "adjust_entitlements":
    case "modify":
      return "adjust";
    default:
      // not_required / non_compliant / not_appropriate / out_of_scope / unknown
      return "neutral";
  }
};

/**
 * The Previous-review cell text beside the outcome icon — "Harry Lucas · 16 Jul"
 * (reviewer · date). Drops a missing segment; "" when neither is known (the cell
 * then shows just the icon). Null when the row has no prior-stage context.
 */
export const previousReviewMeta = (
  account: Pick<
    MFReviewAccount,
    "stage" | "stage1Review" | "stage1ReviewedAt" | "stage1ReviewedByName"
  >,
  now: Date = new Date(),
): string | null => {
  const pr = previousReviewOf(account);
  if (!pr) return null;
  return [pr.reviewedByName, formatStageDate(pr.reviewedAt, now)]
    .filter(Boolean)
    .join(" · ");
};

// Short "12 Jun" style date, or null for a missing/invalid date. Kept local so
// the helper stays pure and testable without pulling a date lib.
const formatStageDate = (
  iso: string | null | undefined,
  now: Date,
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const yearSuffix =
    d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${day} ${month}${yearSuffix}`;
};

/* ------------------------------------------------------------------ *
 * Review page — review STATE (workflow phase, cycle-level)
 *
 * Split from review STATUS (per-account outcome). This derives the workflow
 * banner/state from the review-details payload.
 * ------------------------------------------------------------------ */

export type ReviewState =
  | "not_started"
  | "in_progress"
  | "ready_to_submit"
  | "awaiting_approval"
  | "revision_requested"
  | "complete"
  | "cycle_ended"
  | "read_only";

/**
 * Whether the admin has ended (or archived) the review's cycle, so the
 * reviewer's decisions must be refused (PLO-6551). End Cycle leaves an
 * unsubmitted set `in_progress`, so the set status alone reads as editable;
 * only the cycle says the work is over. The server refuses the writes either
 * way. A legacy review with no cycle, or an older payload without the field,
 * is never ended.
 */
export const isCycleEnded = (
  cycleStatus: MFReviewDetails["cycleStatus"],
): boolean => cycleStatus != null && cycleStatus !== "in_progress";

export interface ReviewStateInput {
  status: MFReviewDetails["review"]["status"];
  accountSetStatus: MFReviewDetails["accountSetStatus"];
  /** Required: an omission would read every ended cycle as editable. */
  cycleStatus: MFReviewDetails["cycleStatus"];
  viewMode?: MFReviewDetails["viewMode"];
  reviewedCount: number;
  totalCount: number;
  /**
   * Local optimistic flag set the moment a submit succeeds, before the server
   * status/accountSetStatus refetch. Treated as awaiting-approval so navigating
   * away right after submitting doesn't warn the review "hasn't been submitted".
   */
  justSubmitted?: boolean;
}

/**
 * Derive the review's workflow STATE. This is the single source of truth for
 * the sticky alert bar, the header meter's success flip, and the
 * ready-to-submit soft-lock.
 */
export const deriveReviewState = ({
  status,
  accountSetStatus,
  cycleStatus,
  viewMode,
  reviewedCount,
  totalCount,
  justSubmitted = false,
}: ReviewStateInput): ReviewState => {
  // Attestors drill in read-only.
  if (viewMode === "attestor") return "read_only";

  if (accountSetStatus === "approved" || status === "complete")
    return "complete";
  // Ahead of every set status below: an ended cycle is no longer the
  // reviewer's to submit, revise or wait on.
  if (isCycleEnded(cycleStatus)) return "cycle_ended";
  if (accountSetStatus === "revision_requested") return "revision_requested";
  if (accountSetStatus === "submitted" || status === "ready_for_approval")
    return "awaiting_approval";
  // Optimistically await approval between a successful submit and the refetch,
  // so the soft-lock/leave-guard don't treat a just-submitted review as still
  // needing submission. A real approved/revision/submitted status above wins.
  if (justSubmitted) return "awaiting_approval";

  // Still editable: is everything reviewed but not yet submitted?
  //
  // A zero total is "ready to submit", not "not started" (PLO-6038). A set can
  // legitimately end up with nothing in it: every row reassigned to another
  // reviewer, or excluded from scope by an admin. The reviewer still has to
  // submit it before the review can leave their hands, so the page must read as
  // ready rather than parking them on a "Not started" review they can never
  // start.
  if (reviewedCount >= totalCount) return "ready_to_submit";
  if (reviewedCount > 0) return "in_progress";
  return "not_started";
};

/**
 * The reviewer's optimistic submission: whether one is outstanding, and the
 * `accountSetStatus` last seen from the server.
 *
 * A submit is optimistic because the table must not flash editable during the
 * refetch that follows it, so the client locks immediately and waits for the
 * server to catch up. The flag therefore has to expire on its own once the
 * server has spoken, and the only reliable signal for that is the status
 * *changing* — which is why the last observed status is carried alongside it.
 *
 * PLO-5591: an admin can request a revision while the reviewer still has the
 * page open. Previously the flag was a bare boolean cleared only when the
 * route's `reviewId` changed, so it outlived its submission: once the revision
 * refetch landed, the table stayed read-only and the bulk approve/reject
 * handlers bailed out silently, against the very revision the banner was
 * asking for, and clearable only by a full page reload.
 *
 * Freezing the status at submit time does not work either. A re-submit made
 * from `revision_requested` and a second revision request both leave the set
 * reading `revision_requested`, so a frozen origin cannot tell "my submit is
 * in flight" from "I have been sent back again" and re-locks the reviewer on
 * every round after the first. Only the sequence distinguishes them.
 */
export interface OptimisticSubmit {
  pending: boolean;
  observedStatus: MFReviewDetails["accountSetStatus"];
}

/** No submission outstanding, anchored to the status currently on screen. */
export const noOptimisticSubmit = (
  observedStatus: MFReviewDetails["accountSetStatus"],
): OptimisticSubmit => ({ pending: false, observedStatus });

/** Record a submit that the server accepted but has not yet reported back. */
export const optimisticSubmitRecorded = (
  state: OptimisticSubmit,
): OptimisticSubmit => ({ ...state, pending: true });

/**
 * Fold in a status from the server. Any change means the refetch has landed,
 * so the optimistic submission has done its job and expires — whether the new
 * status is the expected `submitted` or an admin sending the set back.
 */
export const optimisticSubmitObserved = (
  state: OptimisticSubmit,
  accountSetStatus: MFReviewDetails["accountSetStatus"],
): OptimisticSubmit =>
  accountSetStatus === state.observedStatus
    ? state
    : { pending: false, observedStatus: accountSetStatus };

/**
 * Whether the reviewer's submission is still standing: in flight locally, or
 * confirmed by the server.
 *
 * The `observedStatus` comparison makes this correct in the render between a
 * new status arriving and the fold above running, so the lock never depends on
 * effect ordering.
 */
export const isSubmissionPending = (
  state: OptimisticSubmit,
  accountSetStatus: MFReviewDetails["accountSetStatus"],
): boolean =>
  accountSetStatus === "submitted" ||
  (state.pending && accountSetStatus === state.observedStatus);

/**
 * Whether the set has left the reviewer's hands, so their decisions must be
 * refused. `approved` is terminal and stays locked even where an expired
 * optimistic submission no longer does, and an ended cycle locks whatever the
 * set says (`isCycleEnded`). `cycleStatus` is required for the same reason
 * `deriveReviewState` requires it.
 */
export const isDecisionLocked = (
  state: OptimisticSubmit,
  accountSetStatus: MFReviewDetails["accountSetStatus"],
  cycleStatus: MFReviewDetails["cycleStatus"],
): boolean =>
  isSubmissionPending(state, accountSetStatus) ||
  accountSetStatus === "approved" ||
  isCycleEnded(cycleStatus);

/** Whether the reviewer can still change decisions in this state. */
export const isEditableState = (state: ReviewState): boolean =>
  state === "not_started" ||
  state === "in_progress" ||
  state === "ready_to_submit" ||
  state === "revision_requested";

/** The header progress meter flips to green only when the review is complete. */
export const meterIsComplete = (state: ReviewState): boolean =>
  state === "complete";

/* ------------------------------------------------------------------ *
 * Status pill + relative-date labels (PLO-4803 design fidelity)
 *
 * The campaign card, resource drawer and review-workspace header share ONE
 * status pill and one meta line. "Overdue" is never a pill — it lives in the
 * due text (red pulsing blip). These pure helpers derive the pill kind and the
 * relative-date strings the shared components render, so the wording rules stay
 * testable without rendering.
 * ------------------------------------------------------------------ */

/** The four visual states of the shared review status pill. */
export type StatusPillKind =
  "not_started" | "in_progress" | "awaiting" | "complete";

/** Whole days SINCE `date` (positive for a past date). */
export const daysAgo = (
  date: string | Date,
  now: Date = new Date(),
): number => {
  const then = new Date(date);
  return Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
};

/** "today" / "yesterday" / "Nd ago" for a started-at timestamp. */
export const startedAgoLabel = (
  insertedAt: string | Date,
  now: Date = new Date(),
): string => {
  const d = daysAgo(insertedAt, now);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  return `${d}d ago`;
};

/** "Overdue by Nd" / "Due today" / "Due in Nd" from a whole-day delta. */
export const dueDayLabel = (days: number): string =>
  days < 0
    ? `Overdue by ${Math.abs(days)}d`
    : days === 0
      ? "Due today"
      : `Due in ${days}d`;

/**
 * The campaign card / drawer-header pill kind. A completed campaign is
 * "complete"; a still-open campaign whose resource reviews are all done reads
 * "awaiting" (admin approval); a draft is "not started"; anything else is in
 * progress. `reviewsComplete` must already encode the total > 0 guard
 * (see `isComplete`) so an empty campaign never reads as awaiting.
 */
export const campaignStatusPillKind = (
  status: MFCampaign["status"],
  reviewsComplete: boolean,
): StatusPillKind => {
  if (status === "completed") return "complete";
  if (reviewsComplete) return "awaiting";
  if (status === "draft") return "not_started";
  return "in_progress";
};

/** The resource-review (drawer row) pill kind. */
export const reviewStatusPillKind = (
  status: MFCampaignReview["status"],
): StatusPillKind => {
  if (status === "complete") return "complete";
  if (status === "ready_for_approval") return "awaiting";
  return "in_progress";
};

/** The review-workspace header pill kind, derived from the workflow state. */
export const reviewStatePillKind = (state: ReviewState): StatusPillKind => {
  // An ended cycle is over, as its campaign card already says.
  if (state === "complete" || state === "cycle_ended") return "complete";
  if (state === "awaiting_approval") return "awaiting";
  if (state === "not_started") return "not_started";
  return "in_progress";
};

/* ------------------------------------------------------------------ *
 * Ready-to-submit soft-lock + leave guard
 * ------------------------------------------------------------------ */

/**
 * When a review is fully reviewed but not yet submitted, the table is
 * soft-locked (browse only) behind a submit CTA. An explicit "edit" click
 * unlocks it for further changes.
 */
export const isSoftLocked = (
  state: ReviewState,
  editingOverride: boolean,
): boolean => state === "ready_to_submit" && !editingOverride;

/** Show the leave-confirmation dialog when navigating away un-submitted. */
export const shouldConfirmLeave = (
  state: ReviewState,
  hasUnsavedCorrections: boolean,
): boolean => state === "ready_to_submit" || hasUnsavedCorrections;

export interface LeaveConfirmCopy {
  title: string;
  body: string;
  confirmLabel: string;
}

/**
 * Leave-confirmation copy — the guard can fire for two independent reasons
 * (PLO-4803 finding F2), so the modal names whichever is actually true rather
 * than always describing the ready-to-submit case.
 *
 * `totalCount` of zero is a third: the reviewer has nothing in scope
 * (PLO-6038), so there are no decisions to describe as saved.
 */
export const leaveConfirmCopy = (
  state: ReviewState,
  hasUnsavedCorrections: boolean,
  totalCount: number,
): LeaveConfirmCopy => {
  const reviewReady = state === "ready_to_submit";
  if (reviewReady && !hasUnsavedCorrections && hasNothingInScope(totalCount)) {
    return {
      title: "Leave without submitting?",
      body: "This review has nothing left to decide, but it hasn't been submitted yet. If you leave now, it won't be sent.",
      confirmLabel: "Leave without submitting",
    };
  }
  if (reviewReady && hasUnsavedCorrections) {
    return {
      title: "Leave without saving?",
      body: "This review is complete but hasn't been submitted, and you have unsaved corrections in Fix incorrect data. If you leave now, your decisions are saved but the review won't be sent, and the unsaved corrections will be lost.",
      confirmLabel: "Leave without saving",
    };
  }
  if (hasUnsavedCorrections) {
    return {
      title: "Leave without saving?",
      body: "You have unsaved corrections in Fix incorrect data. If you leave now, these changes will be lost.",
      confirmLabel: "Leave without saving",
    };
  }
  return {
    title: "Leave without submitting?",
    body: "This review is complete but hasn't been submitted yet. If you leave now, your decisions are saved but the review won't be sent.",
    confirmLabel: "Leave without submitting",
  };
};

/* ------------------------------------------------------------------ *
 * Submit prompt once the reviewer finishes (PLO-6222)
 *
 * Reviewers decided every account and then left without submitting, so the
 * review sat outstanding until the deadline. The submit modal now opens by
 * itself when the reviewer's own decision leaves the review ready to submit.
 *
 * Armed only by a decision made on this visit, never by page load: opening
 * an already-finished review must not prompt. Shows at most once per review.
 * ------------------------------------------------------------------ */

export type SubmitPromptPhase = "idle" | "armed" | "shown";

/**
 * Keyed to the review it belongs to. React Router reuses the page across
 * reviews, so a save that resolves after the reviewer moved on — or the first
 * render of the next review — must not act on the wrong one.
 */
export interface SubmitPrompt {
  reviewId: string | undefined;
  phase: SubmitPromptPhase;
}

export const idleSubmitPrompt = (
  reviewId: string | undefined,
): SubmitPrompt => ({
  reviewId,
  phase: "idle",
});

/**
 * A decision on `reviewId` was saved on this visit — arm the prompt, unless it
 * was already shown or the reviewer has since opened another review.
 */
export const armSubmitPrompt = (
  prompt: SubmitPrompt,
  reviewId: string | undefined,
): SubmitPrompt =>
  prompt.reviewId === reviewId && prompt.phase === "idle"
    ? { ...prompt, phase: "armed" }
    : prompt;

export interface SubmitPromptInput {
  reviewId: string | undefined;
  state: ReviewState;
  canSubmit: boolean;
  /** The reviewer clicked Edit on a finished review to change decisions. */
  editing: boolean;
}

/**
 * Whether to open the submit modal now. Only `ready_to_submit` counts: a
 * review sent back for revision already has every row decided, so nothing
 * says when the reviewer is done with it.
 */
export const submitPromptDue = (
  prompt: SubmitPrompt,
  { reviewId, state, canSubmit, editing }: SubmitPromptInput,
): boolean =>
  prompt.reviewId === reviewId &&
  prompt.phase === "armed" &&
  state === "ready_to_submit" &&
  canSubmit &&
  !editing;

/* ------------------------------------------------------------------ *
 * "Nothing left in scope" copy (PLO-6038)
 *
 * A reviewer's set can end up empty: every row reassigned to another reviewer,
 * or excluded from scope by an admin. The reviewer still submits it to close
 * the review off, so every submit affordance keeps working, but "You've
 * reviewed all 0 accounts" is not what happened. These pure helpers pick the
 * wording so the modal and the state banner can never describe the same set
 * differently.
 * ------------------------------------------------------------------ */

/** Whether the reviewer has no accounts left in scope at all. */
export const hasNothingInScope = (totalCount: number): boolean =>
  totalCount === 0;

/** The one sentence every empty-scope affordance opens with. */
const NOTHING_IN_SCOPE =
  "There are no accounts left for you to decide: they were reassigned to another reviewer, or removed from scope.";

/**
 * Whether this reviewer may submit with accounts left undecided. The server
 * already sends `allowUndecidedSubmission` false on the final stage; the
 * `isFinalStage` check keeps the final stage strict even if it ever doesn't.
 */
export const allowsUndecidedSubmission = (
  details: Pick<MFReviewDetails, "allowUndecidedSubmission" | "isFinalStage">,
): boolean =>
  details.allowUndecidedSubmission === true && details.isFinalStage === false;

/**
 * The warning shown before submitting with undecided accounts, or `null` when
 * every account is decided. Counts accounts (access rows), not entitlements or
 * people: a grouped person row can hold several accounts.
 */
export const undecidedAccountsWarning = (
  undecidedCount: number,
  routing: MFUndecidedRowsRouting | null | undefined,
): string | null => {
  if (undecidedCount <= 0) return null;
  const name = routing?.reviewerAssignmentName?.trim();
  const destination = routing
    ? `stage ${routing.nextStage}${name ? ` (${name})` : ""}`
    : "the next stage";
  return undecidedCount === 1
    ? `1 account is undecided. It will be sent to ${destination} as an open row; you will not be able to change it afterwards.`
    : `${undecidedCount} accounts are undecided. They will be sent to ${destination} as open rows; you will not be able to change them afterwards.`;
};

/**
 * The reviewer's submit button. Names the reviewer finishing their part, not
 * the admin sign-off that follows (PLO-6888): "review" and "approval" both
 * read to reviewers as another step they still owe.
 */
export const SUBMIT_DECISIONS_LABEL = "Submit decisions";

export interface SubmitGateInput {
  accountSetId: string | null | undefined;
  accountSetStatus: MFReviewDetails["accountSetStatus"];
  /** End Cycle keeps an unsubmitted set `in_progress`; see `isCycleEnded`. */
  cycleStatus: MFReviewDetails["cycleStatus"];
  reviewedCount: number;
  totalCount: number;
  /** See `allowsUndecidedSubmission`. Lifts the every-row-decided rule only. */
  allowUndecidedSubmission?: boolean;
}

/**
 * Whether the submit button is live. One predicate, read by both the button
 * and the tooltip that explains why it is disabled, so they cannot disagree.
 *
 * Counts, not the completion percentage (PLO-6038). The percentage is rounded
 * to one decimal by the server, so 1999 of 2000 decided reads as exactly 100.0
 * and would open the gate on an incomplete review; and a set with nothing left
 * in scope is complete at 0 of 0 without needing the server to agree that zero
 * over zero is 100%.
 */
export const canSubmitReview = ({
  accountSetId,
  accountSetStatus,
  cycleStatus,
  reviewedCount,
  totalCount,
  allowUndecidedSubmission = false,
}: SubmitGateInput): boolean =>
  !!accountSetId &&
  !isCycleEnded(cycleStatus) &&
  (allowUndecidedSubmission || reviewedCount >= totalCount) &&
  (accountSetStatus === "in_progress" ||
    accountSetStatus === "revision_requested");

export interface SubmitConfirmCopy {
  title: string;
  body: string;
  /** Present only when accounts are being submitted undecided. */
  warning?: string;
}

export interface UndecidedSubmit {
  undecidedCount: number;
  routing: MFUndecidedRowsRouting | null | undefined;
}

const FINISH_YOUR_PART = "Submit your decisions to finish your part.";

/** Title + body for the submit-confirmation modal. */
export const submitConfirmCopy = (
  totalCount: number,
  isInlineAttestation: boolean,
  undecided?: UndecidedSubmit,
): SubmitConfirmCopy => {
  const warning = undecided
    ? undecidedAccountsWarning(undecided.undecidedCount, undecided.routing)
    : null;
  if (undecided && warning) {
    const decided = Math.max(0, totalCount - undecided.undecidedCount);
    return {
      title: isInlineAttestation
        ? "Attest your review"
        : "Some accounts are undecided",
      body: isInlineAttestation
        ? `You've decided ${decided} of ${totalCount} accounts. Confirm the statements below. Your attestation will be recorded with this submission.`
        : `You've decided ${decided} of ${totalCount} accounts in this review. ${FINISH_YOUR_PART}`,
      warning,
    };
  }

  if (hasNothingInScope(totalCount)) {
    return {
      title: isInlineAttestation
        ? "Attest your review"
        : "Nothing left to review",
      body: isInlineAttestation
        ? `${NOTHING_IN_SCOPE} Confirm the statements below to close this review off.`
        : `${NOTHING_IN_SCOPE} Submit this review to finish your part.`,
    };
  }

  return {
    title: isInlineAttestation ? "Attest your review" : "All accounts reviewed",
    body: isInlineAttestation
      ? `You've reviewed all ${totalCount} accounts. Confirm the statements below. Your attestation will be recorded with this submission.`
      : `You've reviewed all ${totalCount} accounts in this review. ${FINISH_YOUR_PART}`,
  };
};

/**
 * Title + body overrides for the ready-to-submit state banner, or `null` to
 * keep the banner's own defaults ("All accounts reviewed / Every account has a
 * decision...").
 */
export const emptyScopeBanner = (
  state: ReviewState,
  totalCount: number,
): { title: string; message: string } | null =>
  state === "ready_to_submit" && hasNothingInScope(totalCount)
    ? {
        title: "Nothing left to review",
        message: `${NOTHING_IN_SCOPE} Submit this review to close it off.`,
      }
    : null;

/* ------------------------------------------------------------------ *
 * Per-account outcome (review STATUS) + "Not required" consent gate
 * ------------------------------------------------------------------ */

export type Outcome = "required" | "not_required" | "out_of_scope";

/**
 * Outcomes that CAN trigger immediate, irreversible de-provisioning. Choosing
 * one demands explicit consent before it's applied, and freezes the row when
 * the cycle actually configures a remediation action for it (see
 * `isAccountDecisionFrozen` / `reversibleOutcomesFor`).
 */
const LOCKING_OUTCOMES: readonly string[] = [
  "not_required",
  "non_compliant",
  "not_appropriate",
];

/**
 * Whether an outcome triggers immediate, irreversible de-provisioning.
 *
 * `decisionOnly` cycles (PLO-5083) only RECORD the reviewer's decision — the
 * backend never deprovisions — so nothing is irreversible and NO outcome locks.
 * Passing `decisionOnly: true` makes this (and its consumers — the reviewer's
 * "will deprovision immediately" warning, the consent gate, and the
 * irreversible row-lock) return "not locking", so a record-decisions-only
 * review stops warning about deprovisioning and lets reviewers freely change a
 * "not required" decision.
 */
export const isLockingOutcome = (
  outcome: string | null | undefined,
  decisionOnly = false,
): boolean => !decisionOnly && !!outcome && LOCKING_OUTCOMES.includes(outcome);

/** "Not required" (and peers) demand an explicit consent checkbox first. */
export const requiresDeprovisionConsent = (
  outcome: Outcome | string,
  decisionOnly = false,
): boolean => isLockingOutcome(outcome, decisionOnly);

/**
 * An outcome option as the cycle stores it (server: `campaign_cycle.outcome_options`,
 * forwarded verbatim to the portal). `actions` is what the backend executes the
 * moment the decision is recorded — with the sole exception of
 * `deprovision_on_approval`, which is a permission for the approving admin
 * rather than an action a decision runs (PLO-5804).
 */
export type OutcomeOptionActions = {
  standard_mapping: string;
  /** The campaign's own label for the outcome; carried but not read here. */
  name?: string;
  actions?: Array<{ type?: string | null } | null> | null;
};

/** Stable identity so a table with nothing reversible keeps its memoised rows. */
export const NO_REVERSIBLE_OUTCOMES: readonly string[] = Object.freeze([]);

/**
 * Whether recording `outcome` on this cycle immediately starts remediation.
 *
 * Mirrors the server: `X.AccessReviews.Campaigns.OutcomeActions.execute_outcome_for_cycle/6`
 * (the single place review-decision remediation happens, shared by the reviewer
 * and admin query-resolution paths) finds the outcome option whose
 * `standard_mapping` matches and runs EVERY action it carries the moment the
 * decision lands — `deprovision` stamps
 * `remediation_status: :in_progress` and emits DeprovisionAccess,
 * `mark_as_removed` emits MarkAsRemoved, `adjust_entitlements` opens a removal
 * task for whichever entitlements the reviewer flagged. Approval itself never
 * remediates, so an outcome carrying no actions has had no effect beyond
 * recording the decision.
 *
 * This reads cycle CONFIG only; `isAccountDecisionFrozen` reads the row's own
 * remediation where the server reports it. The config reading is deliberately
 * conservative: `accountReversibleOutcomes` covers the one config-blind trigger
 * (`ReviewAccounts.execute_outcome_actions/4`'s `mark_as_removed` clause, which
 * stamps remediation without consulting actions at all), and an
 * `adjust_entitlements` outcome with nothing flagged stays frozen even though
 * the server no-opped on it.
 */
/**
 * `deprovision_on_approval` (PLO-5804) is a permission the APPROVAL workflow
 * reads, not an action a decision runs: a cycle offering only that removes
 * nothing when the reviewer decides. Counting it would freeze the row behind
 * "De-provisioning started" while the access is still untouched — the same
 * class of bug PLO-5749 fixed for action-less outcomes.
 */
const APPROVAL_TIME_ONLY: readonly string[] = ["deprovision_on_approval"];

export const startsRemediation = (
  outcome: string | null | undefined,
  outcomeOptions: readonly OutcomeOptionActions[] = [],
): boolean =>
  !!outcome &&
  (
    outcomeOptions.find((option) => option.standard_mapping === outcome)
      ?.actions ?? []
  ).some(
    (action) => !!action?.type && !APPROVAL_TIME_ONLY.includes(action.type),
  );

/**
 * When recording `outcome` on this cycle actually removes the access.
 *
 *  - `"now"`        the outcome carries a decision-time action, so confirming
 *                   starts de-provisioning there and then;
 *  - `"on_approval"` the cycle offers only `deprovision_on_approval`, a
 *                   permission the APPROVING ADMIN exercises later — the
 *                   reviewer's decision removes nothing;
 *  - `"none"`       nothing is configured, the outcome never removed access
 *                   (`required`, `out_of_scope`, an adjustment), or the cycle
 *                   only records decisions.
 *
 * This is what the reviewer's confirm dialog must speak from. Keying its
 * warning, consent gate and consequence sentence off the outcome's NAME told a
 * "deprovision on approval" cycle that confirming "Not required" begins
 * de-provisioning immediately, which it does not (PLO-5804).
 *
 * Fails CLOSED, like `reversibleOutcomesFor`: an outcome the cycle does not
 * offer — including every outcome while `campaignDetails` is still in flight —
 * is treated as removing access now. Its actions may have been edited away
 * AFTER it remediated, and silence is the one answer we can never take back.
 */
export type DecisionRemoval = "now" | "on_approval" | "none";

export const decisionRemovalTiming = (
  outcome: string | null | undefined,
  outcomeOptions: readonly OutcomeOptionActions[] = [],
  decisionOnly = false,
): DecisionRemoval => {
  if (!isLockingOutcome(outcome, decisionOnly)) return "none";

  const option = outcomeOptions.find(
    (candidate) => candidate.standard_mapping === outcome,
  );
  if (!option) return "now";
  if (startsRemediation(outcome, outcomeOptions)) return "now";

  return (option.actions ?? []).some(
    (action) => !!action?.type && APPROVAL_TIME_ONLY.includes(action.type),
  )
    ? "on_approval"
    : "none";
};

/**
 * Does confirming this decision remove the access right now? The single gate
 * behind the reviewer dialog's "De-provisioning starts immediately" warning,
 * its acknowledgement toggle, and the resource owner's "I have already removed
 * this account" certification — none of which may appear on a cycle that
 * removes nothing until an admin approves.
 */
export const decisionRemovesAccessNow = (
  outcome: string | null | undefined,
  outcomeOptions: readonly OutcomeOptionActions[] = [],
  decisionOnly = false,
): boolean =>
  decisionRemovalTiming(outcome, outcomeOptions, decisionOnly) === "now";

/** The confirm dialog's consequence sentence for a removal outcome. */
export const removalConsequenceLine = (removal: DecisionRemoval): string => {
  switch (removal) {
    case "now":
      return "Their access will be removed.";
    case "on_approval":
      return "An admin will decide whether to remove their access when the review is approved.";
    default:
      return "No access is removed by this decision.";
  }
};

/**
 * The locking outcomes that stay REVERSIBLE on this cycle: those the cycle
 * offers AND configures with no actions, so recording one only records the
 * decision — nothing was de-provisioned, and the reviewer may still change
 * their mind in ANY review state, not only during a revision request. A cycle
 * that configures no de-provisioning behaves like "record decisions only" even
 * without the `decisionOnly` flag, and must never freeze a row behind
 * "De-provisioning started" (PLO-5749).
 *
 * Fails CLOSED throughout. An outcome the cycle no longer offers is left frozen
 * (its actions may have been edited away AFTER it remediated), and an empty list
 * unfreezes nothing — `campaignDetails` lands a round-trip after the table first
 * renders, so assuming "nothing remediates" would flash an already-remediated
 * row editable before re-freezing it.
 */
export const reversibleOutcomesFor = (
  outcomeOptions: readonly OutcomeOptionActions[] = [],
): readonly string[] => {
  const reversible = LOCKING_OUTCOMES.filter(
    (outcome) =>
      outcomeOptions.some((option) => option.standard_mapping === outcome) &&
      !startsRemediation(outcome, outcomeOptions),
  );
  return reversible.length === 0 ? NO_REVERSIBLE_OUTCOMES : reversible;
};

/**
 * Narrow a cycle's reversible outcomes to what THIS row may actually re-decide.
 *
 * A row certified as removed is already deprovisioned no matter what the cycle
 * configured: the `mark_as_removed` clause of
 * `ReviewAccounts.execute_outcome_actions/4` stamps deprovision remediation and
 * `status: :removed` without consulting the outcome's actions at all, and
 * `maybe_mark_access_deprovisioned/4` expires the ResourceAccess
 * (`deprovisioned_via: "certified_deprovisioned"`). Such a row stays frozen —
 * flipping it back would erase the removal marker while the access stays
 * revoked (PLO-5749).
 */
export const accountReversibleOutcomes = (
  account: Pick<MFReviewAccount, "status" | "review">,
  reversibleOutcomes: readonly string[] = NO_REVERSIBLE_OUTCOMES,
): readonly string[] =>
  isAccountAlreadyRemoved(account)
    ? NO_REVERSIBLE_OUTCOMES
    : reversibleOutcomes;

/**
 * Whether a decided row is FROZEN from further edits: its outcome kicked off
 * irreversible de-provisioning, so even its own button locks (design B3).
 *
 * Two things unfreeze a locking outcome:
 *  - `decisionOnly` cycles never deprovision at all (PLO-5083), and
 *  - `reversibleOutcomes` — the locking outcomes this cycle configures with no
 *    remediation action, so recording one never started anything. Built by
 *    `reversibleOutcomesFor`; applies in every review state (PLO-5749).
 *
 * This relaxes ONLY the row lock, never the consent gate: `isLockingOutcome`
 * and `requiresDeprovisionConsent` still key off the outcome's name.
 */
export const isDecisionFrozen = (
  outcome: string | null | undefined,
  {
    decisionOnly = false,
    reversibleOutcomes = NO_REVERSIBLE_OUTCOMES,
  }: {
    decisionOnly?: boolean;
    reversibleOutcomes?: readonly string[];
  } = {},
): boolean =>
  !!outcome &&
  isLockingOutcome(outcome, decisionOnly) &&
  !reversibleOutcomes.includes(outcome);

/**
 * Whether THIS row's decision is frozen, read from the removal it actually
 * started rather than from cycle config (PLO-5749).
 *
 * The server calls off a pending or failed `deprovision` when the reviewer
 * re-decides, so such a row stays editable; a completed one, or a row certified
 * as removed, is frozen whatever decision it now carries. Every other row keeps the
 * config-based lock: a row with no remediation may have just been decided (the
 * stamp lands after the response) or revoked by `mark_as_removed`, which stamps
 * nothing, and the server calls off no other remediation type. A payload
 * without `remediationStatus` (an older API) keeps the config-based lock too.
 */
export const isAccountDecisionFrozen = (
  account: Pick<
    MFReviewAccount,
    "status" | "review" | "remediationType" | "remediationStatus"
  >,
  {
    decisionOnly = false,
    reversibleOutcomes = NO_REVERSIBLE_OUTCOMES,
  }: {
    decisionOnly?: boolean;
    reversibleOutcomes?: readonly string[];
  } = {},
): boolean => {
  const configLock = isDecisionFrozen(account.review, {
    decisionOnly,
    reversibleOutcomes: accountReversibleOutcomes(account, reversibleOutcomes),
  });
  if (account.remediationType !== "deprovision") return configLock;
  // A person row with an undecided identity carries no review: its decided
  // identities are frozen one by one, and the undecided one stays open.
  if (account.remediationStatus === "complete") return !!account.review;

  return (
    isLockingOutcome(account.review, decisionOnly) &&
    isAccountAlreadyRemoved(account)
  );
};

const hasPendingRemoval = (
  account: Pick<MFReviewAccount, "remediationType" | "remediationStatus">,
): boolean =>
  account.remediationType === "deprovision" &&
  (account.remediationStatus === "in_progress" ||
    account.remediationStatus === "failed");

/**
 * How many of the selected rows have a removal that has not happened yet, so
 * the confirm dialog can say a decision that keeps the access calls it off
 * (PLO-5749).
 */
export const pendingRemovalCount = (
  selected: readonly Pick<
    MFReviewAccount,
    "remediationType" | "remediationStatus"
  >[],
): number => selected.filter(hasPendingRemoval).length;

/**
 * Whether the server calls off a pending removal for this decision. Mirrors
 * `ReviewAccounts.withdraw_superseded_removals/2`: only a decision that itself
 * deprovisions (or certifies the access removed) leaves it running.
 */
export const decisionCancelsPendingRemoval = (
  outcome: string | null | undefined,
  outcomeOptions: readonly OutcomeOptionActions[] = [],
  { markAsRemoved = false, decisionOnly = false } = {},
): boolean => {
  if (!outcome || markAsRemoved) return false;
  if (decisionOnly) return true;

  return !(
    outcomeOptions.find((option) => option.standard_mapping === outcome)
      ?.actions ?? []
  ).some((action) => action?.type === "deprovision");
};

/**
 * The confirm dialog's notice that this decision cancels pending removals, or
 * null when it cancels none.
 */
export const pendingRemovalNotice = ({
  pendingCount,
  selectedCount,
  noun,
  cancels,
}: {
  pendingCount: number;
  selectedCount: number;
  noun: string;
  cancels: boolean;
}): { title: string; body: string } | null => {
  if (!cancels || pendingCount === 0) return null;
  if (selectedCount === 1) {
    return {
      title: "The pending removal will be cancelled",
      body: "This access has not been removed yet. Confirming cancels its removal.",
    };
  }

  return {
    title:
      pendingCount === 1
        ? "A pending removal will be cancelled"
        : "Pending removals will be cancelled",
    body: `${pendingCount} of the ${selectedCount} selected ${noun}s ${
      pendingCount === 1 ? "has" : "have"
    } not been removed yet. Confirming cancels ${
      pendingCount === 1 ? "its" : "their"
    } removal.`,
  };
};

/**
 * `flagged_note` is what downstream remediation tasks and notifications render.
 * The reviewer's single note doubles as the reason-for-adjustment, so populate
 * it from the note only when entitlements are actually flagged. Otherwise
 * there's nothing to adjust, so send nothing.
 */
export const flaggedNoteForSubmission = (
  note: string,
  flaggedSnapshotIds: readonly string[],
): string | undefined =>
  flaggedSnapshotIds.length > 0 ? note || undefined : undefined;

/* ------------------------------------------------------------------ *
 * Hide-reviewed toggle
 * ------------------------------------------------------------------ */

/**
 * An account counts as "reviewed" once it carries a *decision* outcome. A
 * queried account (`review === "query"`) is parked awaiting an admin response,
 * not decided — it must stay visible under hide-reviewed and must not count
 * toward completion.
 */
export const isReviewed = (review: string | null | undefined): boolean =>
  review != null && review !== "" && review !== "query";

/**
 * A row is "decided" once it carries a decision outcome — this is what removes
 * it from bulk selection: its checkbox goes away and select-all skips it, so a
 * later bulk/alternate action can never overwrite an explicit decision. Keyed
 * on the review outcome (a parked query is not decided; a flag is not an
 * outcome and leaves the row selectable).
 */
export const isAccountDecided = (account: {
  review?: string | null;
}): boolean => isReviewed(account.review);

/** The toggle is only meaningful once at least one account is reviewed. */
export const hideReviewedEnabled = <T extends { review?: string | null }>(
  accounts: T[],
): boolean => accounts.some((a) => isReviewed(a.review));

/** Apply the hide-reviewed toggle to a list of accounts. */
export const applyHideReviewed = <T extends { review?: string | null }>(
  accounts: T[],
  hideReviewed: boolean,
): T[] =>
  hideReviewed ? accounts.filter((a) => !isReviewed(a.review)) : accounts;

/* ------------------------------------------------------------------ *
 * Single-split-panel invariant
 *
 * At most one split panel (corrections OR evidence) may be open at a time.
 * Opening one closes the other. Corrections takes priority when resolving
 * which is mounted.
 * ------------------------------------------------------------------ */

export type SplitPanel = "corrections" | "evidence" | null;

/** Opening `next` yields it; opening the already-open one closes it (toggle). */
export const nextSplitPanel = (
  current: SplitPanel,
  next: Exclude<SplitPanel, null>,
): SplitPanel => (current === next ? null : next);

/** Only one may render; corrections wins if (impossibly) both are set. */
export const resolveMountedPanel = (
  correctionsOpen: boolean,
  evidenceOpen: boolean,
): SplitPanel => {
  if (correctionsOpen) return "corrections";
  if (evidenceOpen) return "evidence";
  return null;
};

/* ------------------------------------------------------------------ *
 * Fix-incorrect-data panel — change cards
 *
 * Corrections auto-save (PLO-4803): there are no drafts. The panel shows a
 * read-only record of what was applied — a change card per applied correction,
 * the flagged-for-removal entitlements, and the removed-not-in-source accounts.
 * ------------------------------------------------------------------ */

export interface EntitlementChange {
  id: string;
  accountId: string;
  accountLabel: string;
  /** entitlement names added in this fix */
  added: string[];
  /** entitlement names removed in this fix */
  removed: string[];
  /** whether this change is a saved/applied fix (shows the "Applied" badge). */
  applied: boolean;
  /** who applied the correction (display name); shown when present. */
  correctedBy?: string;
  /** when the correction was applied (ISO); shown when present. */
  correctedAt?: string;
}

/**
 * The applied entitlement corrections across accounts, one card per correction
 * entry (an account can carry a history of corrections). Reads the server-
 * persisted `entitlementCorrections` list — absent/empty on older payloads
 * yields no cards, so the panel section is single-sourced from server state.
 */
export const appliedCorrectionCards = (
  accounts: MFReviewAccount[],
): EntitlementChange[] =>
  accounts.flatMap((a) =>
    (a.entitlementCorrections ?? []).map((c, i) => ({
      id: `${a.id}-correction-${i}`,
      accountId: a.id,
      accountLabel: reviewAccountDisplayName(a),
      added: c.added ?? [],
      removed: c.removed ?? [],
      applied: true,
      correctedBy: c.correctedBy,
      correctedAt: c.at,
    })),
  );

/** Count of applied correction entries across accounts (for the Fix badge). */
export const appliedCorrectionCount = (accounts: MFReviewAccount[]): number =>
  accounts.reduce((n, a) => n + (a.entitlementCorrections?.length ?? 0), 0);

/* ------------------------------------------------------------------ *
 * Evidence + Luna analysis
 * ------------------------------------------------------------------ */

/** Human-readable file size for evidence cards ("2.4 MB", "812 KB"). */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

export type EvidenceKind = "image" | "pdf" | "csv" | "doc" | "file";

/** Classify an evidence file for its card icon/tint. */
export const evidenceKind = (
  filename: string,
  contentType?: string,
): EvidenceKind => {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const ct = (contentType ?? "").toLowerCase();
  if (
    ct.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp"].includes(ext)
  )
    return "image";
  if (ct.includes("pdf") || ext === "pdf") return "pdf";
  if (ct.includes("csv") || ["csv", "tsv"].includes(ext)) return "csv";
  if (["doc", "docx", "txt", "xls", "xlsx"].includes(ext)) return "doc";
  return "file";
};

/**
 * Luna discrepancies are grouped by type so the reviewer can bulk-act. Returns
 * groups in a stable, sensible order.
 */
export type DiscrepancyType =
  "user_not_in_ploy" | "entitlement_mismatch" | "user_not_in_evidence";

const DISCREPANCY_ORDER: { type: DiscrepancyType; label: string }[] = [
  { type: "user_not_in_ploy", label: "Users missing from Ploy" },
  { type: "entitlement_mismatch", label: "Entitlement mismatches" },
  { type: "user_not_in_evidence", label: "Users not in evidence" },
];

export interface DiscrepancyGroup<D extends { type: string }> {
  type: DiscrepancyType | "other";
  label: string;
  items: D[];
}

/**
 * Bucket discrepancies into the fixed known-type order, then a trailing
 * "other" group for any type not in `DISCREPANCY_ORDER`. Without the catch-all,
 * an unrecognised type is silently dropped from the triage list even though the
 * summary counts still include it — so every discrepancy must land in a group.
 */
export const groupDiscrepancies = <D extends { type: string }>(
  discrepancies: D[],
): DiscrepancyGroup<D>[] => {
  const known = new Set<string>(DISCREPANCY_ORDER.map((d) => d.type));
  const groups: DiscrepancyGroup<D>[] = DISCREPANCY_ORDER.map(
    ({ type, label }) => ({
      type,
      label,
      items: discrepancies.filter((d) => d.type === type),
    }),
  );
  const other = discrepancies.filter((d) => !known.has(d.type));
  if (other.length > 0) {
    groups.push({ type: "other", label: "Other", items: other });
  }
  return groups.filter((g) => g.items.length > 0);
};

/* ------------------------------------------------------------------ *
 * Sticky navigator (review page)
 *
 * The sticky bar carries three things: an icon-only Back, a prev/next pair of
 * resource reviews, and a centred set of position indicators (one dot per
 * resource review in the campaign). This models which dots are complete /
 * submitted / current / upcoming, whether a given dot is navigable, and the
 * prev/next targets — all without touching React.
 * ------------------------------------------------------------------ */

/**
 * How far along a resource review is — PROGRESS ONLY, the ticket's three states
 * (PLO-5595).
 *
 * `complete` and `submitted` are both reviewer-done (see `reviewerIsDone`) and
 * were a single "complete" state until PLO-5595: collapsing them told a
 * reviewer who had submitted 14 of 23 that everything was equally finished,
 * with no way to see what was still parked with the attestor.
 *
 * "You are here" is deliberately NOT one of these. It used to be — a fourth
 * `current` state that the progress states outranked — which meant the dot you
 * were standing on stopped saying where you were as soon as you submitted it.
 * Position and progress are independent facts about a review, so they are two
 * fields: every dot can now show both.
 */
export type NavigatorDotState = "complete" | "submitted" | "upcoming";

export interface NavigatorItem {
  id: string;
  name: string;
  status: MFCampaignReview["status"];
  /** dot appearance — how far along, never where you are */
  dot: NavigatorDotState;
  /** the review on screen; drawn as a ring around whatever `dot` it has */
  isCurrent: boolean;
  /** completed + current dots aren't clickable (nowhere useful to go) */
  navigable: boolean;
}

/** What each progress state is called in the dot's tooltip. */
const DOT_PHRASE: Record<NavigatorDotState, string> = {
  complete: "approved",
  submitted: "submitted, waiting for approval",
  upcoming: "not reviewed yet",
};

/**
 * The dot's tooltip and aria-label (PLO-5595).
 *
 * Lives here rather than in the component, alongside `reviewsLeftLabel`,
 * because it is the only non-colour carrier of the `complete` / `submitted`
 * distinction — the two differ visually by fill and outline alone, so the text
 * is what makes the split legible to a screen reader, or to anyone who cannot
 * tell the two greens apart.
 *
 * Every dot names its own state, where the old copy only did so for the current
 * and completed ones and fell back to "Go to X" for the rest. Clickability is
 * already carried by the pointer cursor and the hover growth; what the strip
 * could not say before is what each dot MEANS, which is the ticket.
 */
export const navigatorDotLabel = (
  dot: NavigatorDotState,
  name: string,
  isCurrent: boolean,
): string =>
  isCurrent
    ? `${name} · ${DOT_PHRASE[dot]} · you're here`
    : `${name} · ${DOT_PHRASE[dot]}`;

/**
 * Build the ordered navigator indicators for a campaign's resource reviews,
 * marking the one matching `currentReviewId` as current.
 *
 * `dot`, `isCurrent` and `navigable` answer three different questions and so
 * share no predicate: how far along the review is, whether it is the one on
 * screen, and whether there is a screen to move to. A review the reviewer has
 * submitted still has a read-only screen, so it stays reachable; only a fully
 * `complete` review (and the current one) is non-navigable.
 *
 * `dot` reads straight off `status` rather than off `reviewerIsDone`, because
 * PLO-5595 needs the two done-states told apart (`complete` = the attestor has
 * approved it, `ready_for_approval` = still with them). `reviewerIsDone` keeps
 * merging them, which is right for COUNTING — see `reviewsLeftCount`.
 */
export const buildNavigatorItems = (
  reviews: Pick<MFCampaignReview, "id" | "name" | "status">[],
  currentReviewId: string | null | undefined,
): NavigatorItem[] =>
  reviews.map((r) => {
    const isCurrent = r.id === currentReviewId;
    // Named for the question it answers, and NOT `isComplete` — that name is
    // already the module's numeric `isComplete(done, total)` helper, which the
    // tab bucketing in this same feature uses.
    const hasNoReviewScreen = r.status === "complete";
    const dot: NavigatorDotState =
      r.status === "complete"
        ? "complete"
        : r.status === "ready_for_approval"
          ? "submitted"
          : "upcoming";
    return {
      id: r.id,
      name: r.name,
      status: r.status,
      dot,
      isCurrent,
      // the current review is already on screen; completed reviews have no
      // review screen to navigate to.
      navigable: !isCurrent && !hasNoReviewScreen,
    };
  });

/**
 * Whether the REVIEWER has finished a resource review (PLO-5326).
 *
 * `ready_for_approval` means they submitted and the attestor now holds it: it
 * is outstanding for the admin, never for them. Mirrors the server's own
 * `completed_reviews` tally (`status in [:complete, :ready_for_approval]`) so
 * the portal's readouts and the API's stats count the same set.
 */
export const reviewerIsDone = (status: MFCampaignReview["status"]): boolean =>
  status === "complete" || status === "ready_for_approval";

/**
 * Count of resource reviews still open (drives the "N reviews left" readout).
 * Counts only what the reviewer can still act on — a review parked with the
 * attestor is not work they have left.
 */
export const reviewsLeftCount = (
  reviews: Pick<MFCampaignReview, "status">[],
): number => reviews.filter((r) => !reviewerIsDone(r.status)).length;

/**
 * The navigator's readout for that count (PLO-5326).
 *
 * At zero the counter is stated affirmatively rather than as "0 reviews left":
 * this is exactly the state a reviewer lands in the moment they submit their
 * last review, and the ticket's ask is that the screen then reads as done, not
 * as a counter that happens to sit at zero. Reached both when every review is
 * `complete` and when they are submitted and awaiting the attestor.
 */
export const reviewsLeftLabel = (left: number): string =>
  left === 0
    ? "All reviews submitted"
    : `${left} ${left === 1 ? "review" : "reviews"} left`;

export interface NavigatorNeighbours<T> {
  prev: T | null;
  next: T | null;
}

/**
 * The previous / next resource review relative to the current one, by list
 * order. Ends of the list yield null (no wrap). Used for the prev/next buttons
 * in the sticky navigator.
 */
export const navigatorNeighbours = <T extends { id: string }>(
  reviews: T[],
  currentReviewId: string | null | undefined,
): NavigatorNeighbours<T> => {
  const idx = reviews.findIndex((r) => r.id === currentReviewId);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? reviews[idx - 1] : null,
    next: idx < reviews.length - 1 ? reviews[idx + 1] : null,
  };
};

/* ------------------------------------------------------------------ *
 * Account drawer — prev/next across the (filtered, ordered) account list
 *
 * The drawer opens focused on one account and can page through the same flat
 * list the table renders (filters + grouping already applied upstream). This
 * is the pure navigation model: given the ordered ids and the open id, what are
 * the neighbours and can we step in each direction?
 * ------------------------------------------------------------------ */

export interface DrawerNav {
  index: number;
  total: number;
  prevId: string | null;
  nextId: string | null;
  hasPrev: boolean;
  hasNext: boolean;
}

/** Resolve the drawer's position + neighbours within the ordered id list. */
export const accountDrawerNav = (
  orderedIds: string[],
  openId: string | null,
): DrawerNav => {
  const index = openId == null ? -1 : orderedIds.indexOf(openId);
  const total = orderedIds.length;
  if (index === -1) {
    return {
      index: -1,
      total,
      prevId: null,
      nextId: null,
      hasPrev: false,
      hasNext: false,
    };
  }
  const prevId = index > 0 ? orderedIds[index - 1] : null;
  const nextId = index < total - 1 ? orderedIds[index + 1] : null;
  return {
    index,
    total,
    prevId,
    nextId,
    hasPrev: prevId !== null,
    hasNext: nextId !== null,
  };
};

/**
 * Step the drawer one account in a direction, returning the new open id (or the
 * current one if there's no neighbour that way). Keeps the drawer from stepping
 * off either end.
 */
export const stepAccountDrawer = (
  orderedIds: string[],
  openId: string | null,
  direction: "prev" | "next",
): string | null => {
  const nav = accountDrawerNav(orderedIds, openId);
  if (direction === "prev") return nav.prevId ?? openId;
  return nav.nextId ?? openId;
};

/* ------------------------------------------------------------------ *
 * Entitlement splitter — which columns are visible
 *
 * The splitter swaps the single "Entitlements" column for one column per
 * entitlement type. The type list is derived from the loaded account pages, so
 * it is recomputed on every refetch — and a refetch is exactly what submitting
 * a review decision triggers. Applying the swap therefore has to be
 * IDEMPOTENT: re-applying it to an already-split column set must be a no-op,
 * not a second insertion (PLO-5210 — the columns duplicated and jumped left
 * each time the reviewer came back to the table).
 * ------------------------------------------------------------------ */

/** Prefix marking a per-entitlement-type column in the visible-column list. */
export const ENTITLEMENT_TYPE_COLUMN_PREFIX = "entitlement_type_";

/** The column id carrying a single entitlement type. */
export const entitlementTypeColumn = (type: string): string =>
  `${ENTITLEMENT_TYPE_COLUMN_PREFIX}${type}`;

export const isEntitlementTypeColumn = (column: string): boolean =>
  column.startsWith(ENTITLEMENT_TYPE_COLUMN_PREFIX);

/**
 * The visible-column list for a given splitter state.
 *
 * Split on: every `entitlement_type_*` column and the combined "entitlements"
 * column are stripped, then the current types are inserted as one block where
 * the first of them sat (falling back to the old "entitlements" slot, then to
 * index 1). Stripping first is what makes it idempotent — the previous code
 * removed only "entitlements", so an already-split list gained a duplicate set
 * on every re-apply.
 *
 * Split off: the reverse — type columns out, a single "entitlements" back in
 * their place.
 *
 * Type columns already on screen KEEP their current order; only types not yet
 * shown are appended, and types that have gone away are dropped. That matters
 * because the caller sorts types by frequency across the loaded pages — as
 * infinite scroll pulls in more accounts the counts shift, and two types with
 * close counts would otherwise swap places under the reviewer mid-review (the
 * other half of PLO-5210's "jump around").
 *
 * Re-applying an unchanged type list returns `prev` ITSELF — same reference, so
 * column positions and drag-resized widths survive a refetch and the
 * virtualized table doesn't re-render for a no-op.
 */
export const entitlementSplitColumns = (
  prev: string[],
  types: string[],
  split: boolean,
): string[] => {
  const firstSplitIndex = prev.findIndex(
    (c) => isEntitlementTypeColumn(c) || c === "entitlements",
  );
  const rest = prev.filter(
    (c) => !isEntitlementTypeColumn(c) && c !== "entitlements",
  );

  // No types to split by (nothing loaded yet, or no entitlements at all) —
  // fall back to the combined column rather than dropping entitlements
  // entirely, which would leave the reviewer with no entitlement data at all.
  let block: string[];
  if (split && types.length > 0) {
    const wanted = types.map(entitlementTypeColumn);
    const wantedSet = new Set(wanted);
    // Survivors first, in the order they are already displayed in; then any
    // genuinely new type, in the caller's order. Deduped, so a column list a
    // reviewer already corrupted under the old build heals on the next apply
    // instead of keeping its duplicates forever.
    const keptSet = new Set<string>();
    const kept = prev.filter((c) => {
      if (!isEntitlementTypeColumn(c) || !wantedSet.has(c)) return false;
      if (keptSet.has(c)) return false;
      keptSet.add(c);
      return true;
    });
    block = [...kept, ...wanted.filter((c) => !keptSet.has(c))];
  } else {
    block = ["entitlements"];
  }

  const insertIndex =
    firstSplitIndex >= 0 ? firstSplitIndex : Math.min(1, rest.length);

  const next = [
    ...rest.slice(0, insertIndex),
    ...block,
    ...rest.slice(insertIndex),
  ];

  const unchanged =
    next.length === prev.length && next.every((c, i) => c === prev[i]);
  return unchanged ? prev : next;
};

/* ------------------------------------------------------------------ *
 * Reviewer column preferences (PLO-5119)
 *
 * Reviewers had to rebuild the same view on every review: turn the columns
 * they wanted on, split the entitlements out, repeat next campaign. Three
 * changes close that.
 *
 * 1. Department and Job title are no longer columns. The Account cell already
 *    renders "Department • Job title" under the member's name, so as columns
 *    they bought a second copy of the same two fields at the cost of two
 *    column widths. Both remain available as Group by options and as filters.
 * 2. Entitlements split by type on their own when the review's entitlements
 *    actually carry types — the state that made the flat view unusable for
 *    entitlement-heavy apps (Benchmark) is now the state that triggers the fix.
 * 3. Whatever the reviewer picks is remembered and applied to their next
 *    review, so the auto-split is a starting point rather than something to
 *    undo every time.
 *
 * The preference is stored per browser, NOT per review — unlike the sibling
 * `accessReview:columnWidths:<id>` / `accessReview:instructionsCollapsed:<id>`
 * keys. Those describe one review's table; this one describes how a reviewer
 * likes to work, and keying it per review would reproduce the exact
 * re-configure-every-campaign problem the ticket is about.
 * ------------------------------------------------------------------ */

/** Columns the reviewer can toggle, in the order the picker lists them. */
export const SELECTABLE_REVIEW_COLUMNS = [
  "member",
  "identity",
  "accessKind",
  "review",
  "entitlements",
  "lastUsed",
  "accessGrantedAt",
] as const;

/** Picker labels, so the option list is derived from the list above, not remirrored. */
export const REVIEW_COLUMN_LABELS: Record<
  (typeof SELECTABLE_REVIEW_COLUMNS)[number],
  string
> = {
  member: "Account",
  identity: "Identity",
  accessKind: "Access",
  review: "Status",
  entitlements: "Entitlements",
  lastUsed: "Last Used",
  accessGrantedAt: "Access Granted",
};

/**
 * Always rendered and pinned last by the table, never offered as a toggle —
 * it holds the row's review controls.
 */
export const PINNED_REVIEW_COLUMN = "actions";

/** Everything on, which is what an unconfigured reviewer now opens on. */
export const DEFAULT_VISIBLE_COLUMNS: string[] = [
  ...SELECTABLE_REVIEW_COLUMNS,
  PINNED_REVIEW_COLUMN,
];

/**
 * Re-pin the row-controls column last. The table normalises this at render
 * time too, but the stored preference has to hold the invariant on its own:
 * re-enabling a hidden column appends it, which would otherwise leave
 * "actions" stranded mid-list, and the Previous-review column is positioned
 * relative to it.
 */
export const ensureActionsLast = (columns: string[]): string[] => {
  const at = columns.indexOf(PINNED_REVIEW_COLUMN);
  // Already last (or absent): return the same reference, so a no-op can't
  // re-render the virtualized table.
  if (at === -1 || at === columns.length - 1) return columns;
  return [
    ...columns.filter((c) => c !== PINNED_REVIEW_COLUMN),
    PINNED_REVIEW_COLUMN,
  ];
};

/**
 * The column frozen at the table's left edge, so a reviewer who scrolls right
 * still sees whose row they are on (PLO-6216).
 */
export const FROZEN_REVIEW_COLUMN = "member";

/**
 * Put the frozen Account column first and the row controls last — the two
 * pinned edges of the table. Re-enabling a hidden column appends it, and a
 * frozen column left mid-table would pin over the columns before it. Applied
 * at render time only: unlike "actions", nothing else is positioned relative
 * to the Account column, and there is no reorder UI to show a stored order.
 * Returns the same reference when nothing moves, so a no-op can't re-render
 * the virtualized table.
 */
export const orderPinnedColumns = (columns: string[]): string[] => {
  const ordered = ensureActionsLast(columns);
  if (ordered.indexOf(FROZEN_REVIEW_COLUMN) <= 0) return ordered;
  return [
    FROZEN_REVIEW_COLUMN,
    ...ordered.filter((c) => c !== FROZEN_REVIEW_COLUMN),
  ];
};

/** The checkbox track that leads every table row. */
export const CHECKBOX_TRACK_PX = 56;

/**
 * `left` offsets of the frozen cells. Each one sits flush against the tracks
 * before it, so no scrolled content shows between them.
 */
export const FROZEN_LEFT_OFFSETS = {
  checkbox: 0,
  member: CHECKBOX_TRACK_PX,
} as const;

/** Gap between the stuck table and the sticky page chrome above it. */
export const STUCK_TABLE_GAP_PX = 12;
/** Below this, a stuck table stops making room for the content under it. */
export const MIN_STUCK_TABLE_PX = 320;
/** Room a frozen Account column must leave for the columns scrolling past it. */
export const MIN_SCROLLING_WIDTH_PX = 200;

/**
 * Where the page's sticky chrome (the TopNav, plus the review's navigator or
 * state bar) ends, from each bar's computed `position`, `top` and height. A bar
 * that is not sticky/fixed scrolls away, so it does not count.
 */
export const stickyChromeBottom = (
  bars: ReadonlyArray<{ position: string; top: number; height: number }>,
): number =>
  bars.reduce(
    (bottom, bar) =>
      bar.position === "sticky" || bar.position === "fixed"
        ? Math.max(bottom, bar.top + bar.height)
        : bottom,
    0,
  );

/**
 * Height cap for a review table stuck `top` px down the viewport (PLO-6216).
 * `below` is the page content under the table's box. At the page's full
 * scroll that content still takes the bottom of the viewport, so a taller table
 * would push its own header up under the sticky chrome.
 *
 * When making that room would squeeze the table under the floor (an expanded
 * previously-reviewed list), it takes the whole viewport under the chrome
 * instead: by the time the page scrolls that far the table is off-screen, so
 * there is no header left to hide.
 *
 * Whole pixels only (PLO-6310). `below` is measured off the table's own box,
 * so a fractional cap fed back into the next measure never settled: the page
 * height flipped by a pixel every few frames and a hovered tooltip shook.
 */
export const stuckTableMaxHeight = (
  viewportHeight: number,
  top: number,
  below: number,
): number => {
  const underChrome = viewportHeight - top;
  const withRoomBelow = underChrome - below;
  return Math.floor(
    withRoomBelow >= MIN_STUCK_TABLE_PX ? withRoomBelow : underChrome,
  );
};

/**
 * Whether a re-measured stuck-table layout can keep the current one
 * (PLO-6310). A cap within a pixel counts as unchanged: when the table's own
 * height sits within a pixel of its cap, each new cap moves the table, which
 * moves the next measure, and the two never agree.
 */
export const sameStuckTableLayout = (
  a: { top: number; maxHeight: number } | null,
  b: { top: number; maxHeight: number } | null,
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    Math.abs(a.maxHeight - b.maxHeight) <= 1);

/** A table that only shares the page with others keeps a fixed cap. */
export const UNFILLED_TABLE_MAX_HEIGHT = "800px";

/**
 * A page-filling table's cap before its stuck layout is measured, and for
 * good on a phone, which never measures one (PLO-6505). The row virtualizer
 * reads its scroll box's height in the same layout pass as that measure,
 * before the measured cap reaches the DOM. An uncapped box is as tall as every
 * row put together, so the virtualizer mounted every row of the review at once
 * and a thousand-account review froze the tab.
 */
export const UNMEASURED_TABLE_MAX_HEIGHT = "100dvh";

/**
 * The CSS max-height of a review table's group box. A page-filling table
 * must never be uncapped: its height is what bounds the virtualized rows.
 */
export const reviewTableMaxHeight = (
  fillsPage: boolean,
  stuckLayout: { maxHeight: number } | null,
): string => {
  if (!fillsPage) return UNFILLED_TABLE_MAX_HEIGHT;
  return stuckLayout
    ? `${stuckLayout.maxHeight}px`
    : UNMEASURED_TABLE_MAX_HEIGHT;
};

/**
 * Whether the Account column can be frozen: the marker, checkbox and Account
 * tracks on the left plus the pinned actions track on the right must still
 * leave room for a column to scroll past. On a phone, or beside a docked side
 * panel, they would cover the whole table.
 */
export const shouldFreezeAccountColumn = (
  scrollportWidth: number,
  accountWidth: number,
  actionsWidth: number,
): boolean =>
  scrollportWidth >=
  FROZEN_LEFT_OFFSETS.member +
    accountWidth +
    actionsWidth +
    MIN_SCROLLING_WIDTH_PX;

const isKnownColumn = (column: string): boolean =>
  column === PINNED_REVIEW_COLUMN ||
  (SELECTABLE_REVIEW_COLUMNS as readonly string[]).includes(column) ||
  // Per-type columns are named after the review's own entitlement types, so
  // they can't be allowlisted here. entitlementSplitColumns reconciles them
  // against the current review's types, which is what keeps them honest.
  isEntitlementTypeColumn(column);

/**
 * Make a column list safe to render.
 *
 * Anything unrecognised is dropped — the migration case being reviewers whose
 * stored list still holds "department"/"jobTitle" (or "accessLevel", removed
 * earlier), which would otherwise fall through AccountRow's switch and render
 * a headerless column of "-". A list with no real columns left falls back to
 * the default rather than leaving a table of nothing but row controls.
 */
export const sanitizeVisibleColumns = (columns: unknown): string[] => {
  if (!Array.isArray(columns)) return DEFAULT_VISIBLE_COLUMNS;

  const seen = new Set<string>();
  const kept = columns.filter((c): c is string => {
    if (typeof c !== "string" || !isKnownColumn(c) || seen.has(c)) return false;
    seen.add(c);
    return true;
  });

  const hasRealColumn = kept.some((c) => c !== PINNED_REVIEW_COLUMN);
  if (!hasRealColumn) return DEFAULT_VISIBLE_COLUMNS;

  return ensureActionsLast([...kept, PINNED_REVIEW_COLUMN]);
};

/**
 * Show or hide one column. Removing the reviewer's last real column is a
 * no-op — the previous inline guard only checked the list length, which the
 * always-present pinned column satisfied on its own, so the table could be
 * stripped down to row controls with no data beside them.
 */
export const toggleVisibleColumn = (
  columns: string[],
  column: string,
): string[] => {
  if (!columns.includes(column)) return ensureActionsLast([...columns, column]);
  const next = columns.filter((c) => c !== column);
  return next.some((c) => c !== PINNED_REVIEW_COLUMN) ? next : columns;
};

/** Whether any loaded row carries indirect access, and so needs the Access column. */
export const hasIndirectAccountRow = (
  accounts: ReadonlyArray<Pick<MFReviewAccount, "accessKind">>,
): boolean => accounts.some(isIndirectAccount);

/**
 * Force the Access column into the RENDERED column list when the review holds
 * indirect rows, without touching the reviewer's stored preference.
 *
 * A reviewer who used the table before this column existed has a saved list
 * that predates it, and sanitizeVisibleColumns only drops unknown columns — it
 * never adds one. Without this they get two identical-looking rows for the same
 * person, no Direct/Indirect chip, and no way to open the access chain (only
 * the Access cell opens it). Positioned where it sits in the defaults.
 */
export const withAccessKindColumn = (
  columns: string[],
  hasIndirect: boolean,
): string[] => {
  if (!hasIndirect || columns.includes("accessKind")) return columns;
  const identityIdx = columns.indexOf("identity");
  const at =
    identityIdx >= 0 ? identityIdx + 1 : Math.max(0, columns.indexOf("review"));
  return [...columns.slice(0, at), "accessKind", ...columns.slice(at)];
};

/**
 * Force the Resource column into the RENDERED column list on a combined review
 * (one review for many resources), right after Account, without touching the
 * reviewer's stored preference — the same trick as withAccessKindColumn. A
 * combined review's rows are about different resources, so a row without its
 * resource cannot be decided.
 */
export const withResourceColumn = (
  columns: string[],
  combined: boolean,
): string[] => {
  if (!combined || columns.includes("resource")) return columns;
  const at = columns.indexOf("member") + 1;
  return [...columns.slice(0, at), "resource", ...columns.slice(at)];
};

/**
 * Whether a combined review's rows need the Resource column. Grouped by
 * person, the resource leads each row in the Account cell instead, so only
 * while that cell is on screen: a reviewer may hide Account, and every row
 * must still say which resource its decision is about.
 */
export const showsResourceColumn = (
  combined: boolean,
  grouping: string,
  columns: string[],
): boolean =>
  combined && (grouping !== "person" || !columns.includes("member"));

/**
 * A combined review's resources, one option each, for "which resource is this
 * account on" when a reviewer adds one: every resource the review's rows are
 * about, by name.
 */
export const reviewResourceOptions = (
  accounts: ReadonlyArray<Pick<MFReviewAccount, "resource">>,
): { value: string; label: string }[] => {
  const byId = new Map<string, string>();
  accounts.forEach(({ resource }) => {
    // A resource's name is optional at source (only its external id is
    // required), and a null label would throw in the sort below.
    if (resource?.id && !byId.has(resource.id))
      byId.set(
        resource.id,
        resource.name || resource.extId || "Unnamed resource",
      );
  });
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * The "Person" grouping's header for a row: the member when there is one
 * (name and email, so two people sharing a name stay apart), else the
 * identity, which is all a service account has.
 */
export const personGroupTitle = (
  account: Pick<MFReviewAccount, "member" | "identity">,
): string => {
  const { member, identity } = account;
  if (member?.name && member.email) return `${member.name} (${member.email})`;
  return (
    member?.name ||
    member?.email ||
    identity?.displayName ||
    identity?.username ||
    identity?.extId ||
    "Unknown person"
  );
};

/**
 * Who a person group is: the member, else the identity, else the row alone.
 * Names are not unique (two service accounts both called "deploy"), and the
 * group header's buttons decide every row in the group, so the key must never
 * merge two people that only share a label.
 */
export const personGroupKey = (
  account: Pick<MFReviewAccount, "id" | "member" | "identity">,
): string => {
  if (account.member?.id) return `member:${account.member.id}`;
  if (account.identity?.id) return `identity:${account.identity.id}`;
  return `row:${account.id}`;
};

/** Whether any entitlement column — combined or per-type — is on screen. */
export const hasEntitlementColumn = (columns: string[]): boolean =>
  columns.some((c) => c === "entitlements" || isEntitlementTypeColumn(c));

/**
 * Reconcile the entitlement block against the current review's types.
 *
 * entitlementSplitColumns always produces a block, which is right when it is
 * healing a list that lost one (PLO-5210) but wrong once the picker can hide
 * Entitlements outright: the column came straight back on the next review, or
 * on any refetch that shifted the type set. A list with no entitlement column
 * is now left exactly as the reviewer left it.
 */
export const reconcileEntitlementColumns = (
  prev: string[],
  types: string[],
  split: boolean,
): string[] =>
  hasEntitlementColumn(prev)
    ? entitlementSplitColumns(prev, types, split)
    : prev;

/**
 * Strip a column list down to what is worth persisting: per-type entitlement
 * columns collapse back to the single combined column, because they name one
 * review's entitlement taxonomy and mean nothing in the next one. The split
 * flag re-expands them per review.
 */
export const columnsForStorage = (columns: string[]): string[] =>
  reconcileEntitlementColumns(columns, [], false);

export type ReviewColumnPrefs = {
  columns: string[] | null;
  splitEntitlementsByType: boolean | null;
};

const NO_COLUMN_PREFS: ReviewColumnPrefs = {
  columns: null,
  splitEntitlementsByType: null,
};

/**
 * Read the stored preference. Both fields are null when the reviewer has never
 * made that choice — a distinction that matters for the splitter, where null
 * ("never chosen", so auto-split) and false ("turned it off") must not
 * collapse into the same value.
 */
export const parseColumnPrefs = (
  raw: string | null | undefined,
): ReviewColumnPrefs => {
  if (!raw) return NO_COLUMN_PREFS;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return NO_COLUMN_PREFS;
    return {
      columns: Array.isArray(parsed.columns)
        ? sanitizeVisibleColumns(parsed.columns)
        : null,
      splitEntitlementsByType:
        typeof parsed.splitEntitlementsByType === "boolean"
          ? parsed.splitEntitlementsByType
          : null,
    };
  } catch {
    return NO_COLUMN_PREFS;
  }
};

export const serializeColumnPrefs = (prefs: ReviewColumnPrefs): string =>
  JSON.stringify(prefs);

/**
 * Whether a type list holds a type worth splitting on.
 *
 * uniqueEntitlementTypes buckets a null `snapshotEntitlementType` under the
 * synthetic "Other", so a review whose entitlements are entirely untyped still
 * produces a one-element list. Auto-splitting on that would hand the reviewer a
 * single column headed "Other" — strictly worse than the combined column it
 * replaced. Only a genuine type counts.
 */
export const hasRealEntitlementTypes = (types: string[]): boolean =>
  types.some((t) => t !== "Other");

/**
 * Whether to open split by type: split when this review's entitlements carry
 * real types, unless the reviewer has turned it off.
 *
 * The real-types check binds a stored `true` as well as the default. A
 * reviewer who switched splitting on somewhere else would otherwise carry it
 * onto a review where nothing is typed, and every entitlement there buckets
 * under the synthetic "Other" — one column headed "Other" in place of the
 * combined one. The preference is kept, not cleared, so it applies again on
 * the next review that does have types.
 */
export const resolveSplitEntitlements = (
  stored: boolean | null,
  hasEntitlementTypes: boolean,
): boolean => hasEntitlementTypes && stored !== false;

/* ------------------------------------------------------------------ *
 * Resizable columns — persisted width overrides
 *
 * Columns (and the entitlement-splitter's sub-columns) can be drag-resized;
 * the chosen widths persist to localStorage. This models the width map: reading
 * a resolved width, clamping a drag to a sane minimum, applying a resize, and
 * round-tripping the map through JSON safely (bad/foreign entries dropped).
 * ------------------------------------------------------------------ */

/** Widths below this collapse a column into unreadability — clamp floor. */
export const MIN_COLUMN_WIDTH = 72;

export type ColumnWidths = Record<string, number>;

/** The width to render a column at: an explicit override, else the default. */
export const resolveColumnWidth = (
  widths: ColumnWidths,
  columnId: string,
  defaultWidth: number,
): number => {
  const w = widths[columnId];
  return typeof w === "number" && Number.isFinite(w)
    ? Math.max(MIN_COLUMN_WIDTH, w)
    : defaultWidth;
};

/** Apply a drag delta to a column's start width, clamped to the minimum. */
export const applyColumnResize = (
  widths: ColumnWidths,
  columnId: string,
  startWidth: number,
  deltaPx: number,
): ColumnWidths => ({
  ...widths,
  [columnId]: Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + deltaPx)),
});

/**
 * Freeze a set of measured column widths as explicit overrides (used at the
 * start of a drag so flex-grow siblings stop redistributing — only the dragged
 * column changes). Existing overrides win over freshly-measured widths.
 */
export const freezeColumnWidths = (
  widths: ColumnWidths,
  measured: ColumnWidths,
): ColumnWidths => ({ ...measured, ...widths });

/** Serialize widths for localStorage. */
export const serializeColumnWidths = (widths: ColumnWidths): string =>
  JSON.stringify(widths);

/**
 * Parse a persisted width map, tolerating garbage: non-object JSON, non-number
 * values and sub-minimum widths are dropped so a corrupt entry can never break
 * the table layout.
 */
export const parseColumnWidths = (
  raw: string | null | undefined,
): ColumnWidths => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return {};
    const out: ColumnWidths = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (
        typeof v === "number" &&
        Number.isFinite(v) &&
        v >= MIN_COLUMN_WIDTH
      ) {
        out[k] = Math.round(v);
      }
    }
    return out;
  } catch {
    return {};
  }
};

/* ------------------------------------------------------------------ *
 * Risk banding (PLO-4816)
 *
 * Resources carry an `app_permission_risk_score` (0–100 integer). The
 * reviewer-side filter bands it low / medium / high. A resource with no score
 * (null/undefined — the app was never risk-scored) has no band and is only
 * matched by the "all" (no filter) selection, never by an explicit band.
 * ------------------------------------------------------------------ */

export type RiskBand = "low" | "medium" | "high";

/** Inclusive lower bounds: <34 low, 34–66 medium, ≥67 high. */
export const RISK_BAND_THRESHOLDS = { medium: 34, high: 67 } as const;

/**
 * Band a raw risk score. Returns null for a missing/non-finite score so a
 * band filter can exclude un-scored resources without a magic sentinel.
 */
export const riskBandOf = (
  score: number | null | undefined,
): RiskBand | null => {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  if (score >= RISK_BAND_THRESHOLDS.high) return "high";
  if (score >= RISK_BAND_THRESHOLDS.medium) return "medium";
  return "low";
};

export const RISK_BAND_LABEL: Record<RiskBand, string> = {
  low: "Low risk",
  medium: "Medium risk",
  high: "High risk",
};

/* ------------------------------------------------------------------ *
 * Campaigns-page review filters (PLO-4816)
 *
 * A campaign's resource-review list (CampaignDetails) can be filtered by
 * application/resource (the review's resource id) and by risk band. Filters
 * compose with AND. Empty selections mean "no constraint on this axis", so an
 * unset filter never hides anything. State round-trips through the URL query so
 * a reviewer's filtered view survives a refresh / shared link.
 * ------------------------------------------------------------------ */

export interface ReviewListFilter {
  /** resource ids to keep; empty = all applications/resources */
  resourceIds: string[];
  /** risk bands to keep; empty = all bands (and un-scored resources) */
  riskBands: RiskBand[];
}

export const EMPTY_REVIEW_FILTER: ReviewListFilter = {
  resourceIds: [],
  riskBands: [],
};

/** Any active constraint? Drives the "Clear filters" affordance + count. */
export const hasActiveReviewFilter = (f: ReviewListFilter): boolean =>
  f.resourceIds.length > 0 || f.riskBands.length > 0;

/**
 * Does a single review pass the filter? Resource-id and risk-band axes are
 * independent AND-ed constraints; an empty axis is a pass. A risk-band filter
 * excludes resources with no score (`riskBandOf` → null).
 */
export const reviewMatchesFilter = (
  review: Pick<MFCampaignReview, "resource">,
  filter: ReviewListFilter,
): boolean => {
  if (
    filter.resourceIds.length > 0 &&
    !filter.resourceIds.includes(review.resource?.id)
  ) {
    return false;
  }
  if (filter.riskBands.length > 0) {
    const band = riskBandOf(review.resource?.appPermissionRiskScore);
    if (band === null || !filter.riskBands.includes(band)) return false;
  }
  return true;
};

/** Apply the filter to a review list, preserving order. */
export const applyReviewFilter = <T extends Pick<MFCampaignReview, "resource">>(
  reviews: T[],
  filter: ReviewListFilter,
): T[] => reviews.filter((r) => reviewMatchesFilter(r, filter));

/**
 * Distinct application/resource options present in a review list, deduped by
 * resource id and sorted by name — feeds the application filter dropdown so it
 * only ever offers resources the reviewer actually has in this campaign.
 */
export interface ResourceFilterOption {
  id: string;
  name: string;
  type: string;
}

export const resourceFilterOptions = (
  reviews: Pick<MFCampaignReview, "resource">[],
): ResourceFilterOption[] => {
  const byId = new Map<string, ResourceFilterOption>();
  for (const r of reviews) {
    const res = r.resource;
    if (res?.id && !byId.has(res.id)) {
      byId.set(res.id, { id: res.id, name: res.name ?? "", type: res.type });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/* --- URL query round-trip (repo convention: filter state in the URL) ---- *
 * Encoded as comma-joined tokens under stable keys so links are readable and
 * shareable. Parsing tolerates junk: unknown risk bands are dropped, blanks
 * collapse away. Keys are namespaced `review_` to sit alongside the existing
 * `review_sort_by` / `review_group_by` params without clashing.
 */

export const REVIEW_FILTER_PARAM_KEYS = {
  resource: "review_resource",
  risk: "review_risk",
} as const;

const RISK_BANDS: readonly RiskBand[] = ["low", "medium", "high"];

const splitTokens = (raw: string | null | undefined): string[] =>
  (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** Read a ReviewListFilter out of URLSearchParams-style getter. */
export const parseReviewFilterFromParams = (
  get: (key: string) => string | null,
): ReviewListFilter => {
  const resourceIds = [
    ...new Set(splitTokens(get(REVIEW_FILTER_PARAM_KEYS.resource))),
  ];
  const riskBands = [
    ...new Set(splitTokens(get(REVIEW_FILTER_PARAM_KEYS.risk))),
  ].filter((b): b is RiskBand => (RISK_BANDS as readonly string[]).includes(b));
  return { resourceIds, riskBands };
};

/**
 * The param key→value pairs a filter should set (empty axis → null, meaning
 * "delete this key"). The caller merges these into the existing params so the
 * sort/group params are untouched.
 */
export const reviewFilterToParamUpdates = (
  filter: ReviewListFilter,
): Array<[string, string | null]> => [
  [
    REVIEW_FILTER_PARAM_KEYS.resource,
    filter.resourceIds.length ? filter.resourceIds.join(",") : null,
  ],
  [
    REVIEW_FILTER_PARAM_KEYS.risk,
    filter.riskBands.length ? filter.riskBands.join(",") : null,
  ],
];

/** Toggle one value in a filter axis (add if absent, remove if present). */
export const toggleFilterValue = <T extends string>(
  values: T[],
  value: T,
): T[] =>
  values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value];

/* ------------------------------------------------------------------ *
 * Query surfacing — reviewer-side chip state (PLO-4816)
 *
 * A reviewer can send an account to the IAM team as a "query" (the question is
 * the account note). The IAM admin answers by writing `adminResponse` +
 * `queryResolvedAt`, at which point the account returns to `requires_review`
 * for the reviewer to re-decide informed. This derives the chip state for a
 * row from the query fields the members API renders.
 *   - "queried":  raised, not yet answered → a neutral "Queried" chip.
 *   - "answered": answered AND back in requires_review → an emphasis
 *                 "Response received" chip prompting the reviewer to act.
 *   - null:       no query in play → no chip.
 * ------------------------------------------------------------------ */

export type QueryChipState = "queried" | "answered" | null;

export type QueryAccount = Pick<
  MFReviewAccount,
  "status" | "review" | "queriedAt" | "adminResponse" | "queryResolvedAt"
>;

const isNonEmpty = (v: string | null | undefined): v is string =>
  typeof v === "string" && v.trim().length > 0;

/** Was this account ever sent to the IAM team as a query? */
export const isQueried = (
  a: Pick<QueryAccount, "queriedAt" | "review">,
): boolean => isNonEmpty(a.queriedAt) || a.review === "query";

/**
 * Has the IAM team answered the CURRENT query? A query is answered only once
 * `queryResolvedAt` is stamped. Re-raising a query clears `queryResolvedAt` but
 * deliberately keeps `adminResponse` for history, so `adminResponse` alone must
 * NOT mark the reopened query answered (the stale answer stays available in the
 * response-history modal via `showsAdminResponse`).
 */
export const hasAdminResponse = (
  a: Pick<QueryAccount, "queryResolvedAt">,
): boolean => isNonEmpty(a.queryResolvedAt);

/**
 * Chip state for a row. An answered query that has come back to
 * `requires_review` is the actionable "Response received" case; an answered
 * query on an already-decided row is spent and shows nothing. A raised-but-
 * unanswered query shows the neutral "Queried" chip.
 */
export const queryChipState = (a: QueryAccount): QueryChipState => {
  const queried = isQueried(a);
  if (!queried) return null;
  const answered = hasAdminResponse(a);
  if (answered) {
    return a.status === "requires_review" ? "answered" : null;
  }
  return "queried";
};

/** Should the drawer / row expansion show the admin's response block? */
export const showsAdminResponse = (
  a: Pick<QueryAccount, "adminResponse">,
): boolean => isNonEmpty(a.adminResponse);

/* ------------------------------------------------------------------ *
 * Remove-a-user chooser eligibility (PLO-4803)
 *
 * The "Remove a user who doesn't exist" chooser must not offer accounts that
 * are already removed/corrected — the server rejects them idempotently, but the
 * UI shouldn't invite a double-attempt. The remove-non-existent correction
 * stamps status `already_removed` + review `"removed"`.
 * ------------------------------------------------------------------ */

export const isAccountAlreadyRemoved = (
  account: Pick<MFReviewAccount, "status" | "review">,
): boolean =>
  account.status === "already_removed" ||
  account.status === "removed" ||
  (account.review as string | null | undefined) === "removed";

/** Accounts eligible for the remove-a-user chooser (not already removed). */
export const accountsSelectableForRemoval = <
  T extends Pick<MFReviewAccount, "status" | "review">,
>(
  accounts: T[],
): T[] => accounts.filter((a) => !isAccountAlreadyRemoved(a));

/**
 * Whether any account carries a removed / already-removed outcome — gates the
 * "Hide removed accounts" toggle (it's meaningless when nothing is removed).
 */
export const hasRemovedAccounts = <
  T extends Pick<MFReviewAccount, "status" | "review">,
>(
  accounts: T[],
): boolean => accounts.some(isAccountAlreadyRemoved);

/**
 * Apply the "Hide removed accounts" toggle: drop rows whose outcome is a removal
 * (review "removed", or status already_removed / removed). Display-only — the
 * server-derived actionable completion counts are unaffected. Composes with
 * applyHideReviewed (both on = both filters).
 */
export const applyHideRemoved = <
  T extends Pick<MFReviewAccount, "status" | "review">,
>(
  accounts: T[],
  hideRemoved: boolean,
): T[] =>
  hideRemoved ? accounts.filter((a) => !isAccountAlreadyRemoved(a)) : accounts;

/** Count accounts with an answered query still awaiting the reviewer's re-decision. */
export const answeredQueryCount = (accounts: QueryAccount[]): number =>
  accounts.reduce((n, a) => (queryChipState(a) === "answered" ? n + 1 : n), 0);

/** Whether any account has an answered query awaiting the reviewer (drives the
 *  "View responses" affordance). */
export const hasAnsweredResponses = (accounts: QueryAccount[]): boolean =>
  accounts.some((a) => queryChipState(a) === "answered");

/**
 * The IAM team's answered-query responses awaiting the reviewer's re-decision,
 * as flat entries for the "View responses" modal. One per account whose query
 * was answered and is back in requires_review (queryChipState "answered").
 * `answeredBy` is absent on older payloads that predate the field — the
 * modal omits the "Answered by" line rather than showing "unknown".
 */
export interface QueryResponseEntry {
  accountId: string;
  name: string;
  email: string | null;
  /** the reviewer's original question (the query note). */
  query: string | null;
  /** the IAM team's answer. */
  answer: string | null;
  /** who answered (display name). Null when the payload doesn't carry it. */
  answeredBy: string | null;
  /** when the query was resolved. */
  resolvedAt: string | null;
}

export const queryResponseEntries = (
  accounts: MFReviewAccount[],
): QueryResponseEntry[] =>
  accounts
    .filter((a) => queryChipState(a) === "answered")
    .map((a) => ({
      accountId: a.id,
      name: reviewAccountDisplayName(a),
      email: a.member?.email ?? null,
      query: a.note ?? null,
      answer: a.adminResponse ?? null,
      answeredBy: a.answeredBy ?? null,
      resolvedAt: a.queryResolvedAt ?? null,
    }));

/** Count accounts currently out with the IAM team (queried, not yet answered). */
export const openQueryCount = (accounts: QueryAccount[]): number =>
  accounts.reduce((n, a) => (queryChipState(a) === "queried" ? n + 1 : n), 0);

/* ------------------------------------------------------------------ *
 * General (account-less) review queries (PLO-4803)
 *
 * Separate from the per-account query above: a reviewer can raise a query about
 * the REVIEW AS A WHOLE to the campaign admin — the note IS the question. It
 * stays OPEN (`resolvedAt` null) until the admin answers, at which point
 * `adminResponse` + `resolvedAt` (+ `answeredBy`) are populated. An OPEN general
 * query blocks submitting the review for approval (enforced server-side); an
 * answered one surfaces in the shared "View responses" modal. These pure helpers
 * derive the open/answered split + the response entries so the header chips and
 * modal stay presentational.
 * ------------------------------------------------------------------ */

export interface GeneralQuery {
  id: string;
  /** the reviewer's question. */
  note: string | null;
  raisedAt?: string | null;
  /** the admin's answer, once resolved. */
  adminResponse?: string | null;
  /** set when the admin resolves the query — its presence flips open → answered. */
  resolvedAt?: string | null;
  /** who answered (display name). Absent on older payloads. */
  answeredBy?: string | null;
}

/** A general query is OPEN (awaiting the admin) while it has no resolved date. */
export const isGeneralQueryOpen = (
  q: Pick<GeneralQuery, "resolvedAt">,
): boolean => !isNonEmpty(q.resolvedAt);

/** The still-open general queries (awaiting the admin's response). */
export const openGeneralQueries = <T extends Pick<GeneralQuery, "resolvedAt">>(
  queries: T[] | null | undefined,
): T[] => (queries ?? []).filter(isGeneralQueryOpen);

/**
 * Whether any general query is still open. Drives the "Query awaiting response"
 * header chip and mirrors the server-side submit block.
 */
export const hasOpenGeneralQuery = (
  queries: Pick<GeneralQuery, "resolvedAt">[] | null | undefined,
): boolean => (queries ?? []).some(isGeneralQueryOpen);

/**
 * Whether any general query has been answered (resolved). Drives the
 * "Response received" header chip (shown only when nothing is still open).
 */
export const hasAnsweredGeneralQuery = (
  queries: Pick<GeneralQuery, "resolvedAt">[] | null | undefined,
): boolean => (queries ?? []).some((q) => !isGeneralQueryOpen(q));

/** The literal name a general query renders under (it has no account). */
export const GENERAL_QUERY_ENTRY_NAME = "General query";

/**
 * The answered general queries as flat `QueryResponseEntry` items for the shared
 * "View responses" modal — merged with `queryResponseEntries` at the call site
 * so account and review-scope responses share one list + count. General queries
 * have no account, so `name` is the literal "General query" and `email` is null;
 * the id is namespaced (`general:<id>`) so it can never collide with an account
 * entry's React key.
 */
export const generalQueryEntries = (
  queries: GeneralQuery[] | null | undefined,
): QueryResponseEntry[] =>
  (queries ?? [])
    .filter((q) => !isGeneralQueryOpen(q))
    .map((q) => ({
      accountId: `general:${q.id}`,
      name: GENERAL_QUERY_ENTRY_NAME,
      email: null,
      query: q.note ?? null,
      answer: q.adminResponse ?? null,
      answeredBy: q.answeredBy ?? null,
      resolvedAt: q.resolvedAt ?? null,
    }));

/**
 * Row/drawer action id for "Raise a query" (PLO-4803 P4). Routed through the
 * same review handler as the outcome actions, but intercepted to open the query
 * modal rather than the decision flow — the query posts `review: "query"` with
 * the reviewer's note as the question.
 */
export const RAISE_QUERY_ACTION = "raise_query";

/**
 * The query note is mandatory — the note IS the question sent to the IAM team,
 * so a blank note has nothing to ask. Non-blank after trimming.
 */
export const isQueryNoteValid = (note: string | null | undefined): boolean =>
  typeof note === "string" && note.trim().length > 0;

/* --- row action sequence (outcome icons + query) ----------------------- *
 * The per-row action set is the campaign's configured outcomes (sorted into a
 * canonical order) followed ALWAYS by the "Raise a query" action — so the query
 * icon renders on every actionable row regardless of the outcome config. */

// Canonical action order (design ACTION_ICONS): green check (required) → red X
// (not required) → orange flag (adjust) → grey minus (out of scope), with any
// other configured outcome trailing.
const ACTION_ORDER_RANK: Record<string, number> = {
  required: 0,
  keep: 0,
  compliant: 0,
  appropriate: 0,
  not_required: 1,
  non_compliant: 1,
  not_appropriate: 1,
  remove: 1,
  adjust_entitlements: 2,
  modify: 2,
  out_of_scope: 3,
  removed: 3,
};

export const actionOrderRank = (mapping: string): number =>
  ACTION_ORDER_RANK[mapping] ?? 4;

export interface RowActionItem {
  kind: "outcome" | "query";
  /** the action id passed to the handler (standard_mapping, or RAISE_QUERY_ACTION). */
  action: string;
}

/**
 * The ordered row actions: the configured outcomes (canonical order) then the
 * query action, ALWAYS last. The query action is unconditional — even an empty
 * outcome config yields a single query action — so the "Raise a query" icon can
 * never be dropped by a campaign's outcome configuration.
 */
export const rowActionSequence = (
  outcomeOptions: OutcomeMappingLike[],
  entitlementOnly = false,
): RowActionItem[] => [
  ...[...selectableOutcomeOptions(outcomeOptions, entitlementOnly)]
    .sort(
      (a, b) =>
        actionOrderRank(a.standard_mapping) -
        actionOrderRank(b.standard_mapping),
    )
    .map((o): RowActionItem => ({
      kind: "outcome",
      action: o.standard_mapping,
    })),
  { kind: "query", action: RAISE_QUERY_ACTION },
];

/** Number of action buttons a row renders (outcomes + the query action). */
export const rowActionButtonCount = (
  outcomeOptions: OutcomeMappingLike[],
  entitlementOnly = false,
): number =>
  selectableOutcomeOptions(outcomeOptions, entitlementOnly).length + 1;

/* --- entitlement-only cycles (PLO-5731) ------------------------------- *
 * A row IS one entitlement, so "flag some of this account's entitlements for
 * adjustment" has nothing left to choose between — the row's only entitlement
 * is the one being decided, and a removal decision already takes away just
 * that one. The adjustment outcome is therefore dropped from every place a
 * reviewer can pick an outcome, and the decision dialog says what a removal
 * actually does. These helpers are the whole of that difference. */

/**
 * The outcomes a reviewer may pick in this cycle. Identical to the campaign's
 * configured list except in an entitlement-only cycle, where the
 * adjust-entitlements outcome is dropped (both spellings — the standard
 * mapping and a custom-named outcome carrying the action).
 */
export const selectableOutcomeOptions = <T extends OutcomeMappingLike>(
  outcomeOptions: readonly T[],
  entitlementOnly = false,
): readonly T[] =>
  entitlementOnly
    ? outcomeOptions.filter((o) => !isEntitlementAdjustmentOutcome(o))
    : outcomeOptions;

/**
 * The tooltip a row's outcome button carries. Only removal outcomes differ,
 * and only in an entitlement-only cycle: confirming one takes away the row's
 * single entitlement, not the account, so a "Not appropriate" label that reads
 * as losing the whole account would overstate what happens.
 */
export const removalActionTooltip = (
  mapping: string,
  label: string,
  entitlementOnly = false,
): string =>
  entitlementOnly && isLockingOutcome(mapping)
    ? "Remove this entitlement"
    : label;

/**
 * Does the decision dialog offer the flag-entitlements picker? Never in an
 * entitlement-only cycle: there is exactly one entitlement on the row and it is
 * already what the decision is about, so the picker would ask the reviewer to
 * re-select the only thing on screen.
 */
export const offersEntitlementFlagging = (
  isAdjustmentOutcome: boolean,
  flaggableCount: number,
  entitlementOnly = false,
): boolean => isAdjustmentOutcome && flaggableCount > 0 && !entitlementOnly;

/**
 * How many of the selected accounts an adjustment empties, and so converts into
 * a removal (PLO-5461). Always zero in an entitlement-only cycle: nothing can
 * be flagged there, and the server's conversion rule does not apply to a row
 * that reviews one entitlement — its removal already IS scoped to that
 * entitlement, so re-labelling it a full account removal would be wrong.
 */
export const adjustmentConversionCount = (
  accountEntitlementIds: readonly (readonly string[])[],
  flaggedIds: readonly string[],
  entitlementOnly = false,
): number =>
  entitlementOnly
    ? 0
    : accountsFullyFlaggedCount(accountEntitlementIds, flaggedIds);

/** What a decision dialog is deciding about, named for the reviewer. */
export const decisionSubject = (
  selectedCount: number,
  accountName: string | null | undefined,
  entitlementOnly = false,
): string => {
  const noun = entitlementOnly ? "entitlement" : "account";
  if (selectedCount > 1) return `${selectedCount} ${noun}s`;
  return accountName || `this ${noun}`;
};

/**
 * What confirming a removal does to an entitlement-only row, in the reviewer's
 * own terms. Names the entitlement being taken away and says the rest of the
 * account is untouched — the sentence a reviewer needs before removing one role
 * from a person who holds several.
 *
 * `removal` is the cycle's configured timing, exactly as everywhere else: a
 * cycle that only records decisions, or defers removal to the approving admin,
 * must not be told the entitlement is going away now (PLO-5804 / PLO-5083).
 */
export const entitlementRemovalLine = ({
  entitlementLabel,
  personName,
  selectedCount,
  removal,
}: {
  entitlementLabel?: string | null;
  personName?: string | null;
  selectedCount: number;
  removal: DecisionRemoval;
}): string => {
  if (removal === "none") return removalConsequenceLine(removal);

  const bulk = selectedCount > 1;
  const subject = bulk
    ? "the selected entitlements"
    : entitlementLabel
      ? `the ${entitlementLabel} entitlement`
      : "this entitlement";
  const owner = bulk
    ? "the selected accounts"
    : personName
      ? `${personName}'s account`
      : "this account";

  const action =
    removal === "now"
      ? `This will remove ${subject} from ${owner}.`
      : `An admin can remove ${subject} from ${owner} when the review is approved.`;

  return `${action} Other entitlements are not affected.`;
};

/**
 * The chip label for one entitlement — "Mortgages Production: Auditor", or just
 * the name when the snapshot carries no type. The typed form the row chip and
 * the decision dialog both read from, so they can never disagree.
 */
export const entitlementChipLabel = (entitlement: {
  snapshotEntitlementName: string;
  snapshotEntitlementType?: string | null;
}): string => {
  const { type, value } = entitlementChipParts(entitlement);
  return type ? `${type}: ${value}` : value;
};

/* ------------------------------------------------------------------ *
 * Bulk "mark as non-existing" (PLO-4803)
 *
 * The remove-non-existent correction has no bulk endpoint — it's called once per
 * account. The server rejects accounts the reviewer can't correct (their own
 * account, or ones already reviewed/corrected), so a bulk run must tolerate
 * per-account failure. This partitions the settled results (by index, parallel
 * to the account ids) into the accounts that were corrected and the ones that
 * were rejected + why, so the caller can surface an honest succeeded/failed
 * summary.
 * ------------------------------------------------------------------ */

export interface BulkRemoveFailure {
  accountId: string;
  reason: string;
}

export interface BulkRemoveOutcome {
  succeeded: string[];
  failed: BulkRemoveFailure[];
}

const settledFailureReason = (result: PromiseRejectedResult): string => {
  const reason = result.reason;
  if (reason instanceof Error && reason.message) return reason.message;
  if (typeof reason === "string" && reason.trim()) return reason;
  if (reason && typeof reason === "object" && "message" in reason) {
    const message = (reason as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "Couldn't be corrected";
};

export const partitionSettledByAccount = (
  accountIds: string[],
  settled: PromiseSettledResult<unknown>[],
): BulkRemoveOutcome => {
  const succeeded: string[] = [];
  const failed: BulkRemoveFailure[] = [];
  accountIds.forEach((accountId, i) => {
    const result = settled[i];
    if (result && result.status === "fulfilled") {
      succeeded.push(accountId);
    } else {
      failed.push({
        accountId,
        reason:
          result && result.status === "rejected"
            ? settledFailureReason(result)
            : "Couldn't be corrected",
      });
    }
  });
  return { succeeded, failed };
};

/**
 * A one-line summary of a bulk mark-as-non-existing run for a toast, e.g.
 * "Marked 3 accounts as not existing. 1 couldn't be corrected." Singularises
 * and omits the failure clause when everything succeeded.
 */
export const bulkRemoveSummary = (outcome: BulkRemoveOutcome): string => {
  const s = outcome.succeeded.length;
  const f = outcome.failed.length;
  const parts: string[] = [];
  if (s > 0) {
    parts.push(
      `Marked ${s} ${s === 1 ? "account" : "accounts"} as not existing.`,
    );
  }
  if (f > 0) {
    parts.push(
      `${f} ${f === 1 ? "account" : "accounts"} couldn't be corrected.`,
    );
  }
  return parts.join(" ") || "No accounts were corrected.";
};

/**
 * A one-line summary of an apply-corrections run for a toast, e.g. "Fixed 3
 * accounts. 1 account couldn't be updated." Singularises and omits the failure
 * clause when everything succeeded.
 */
export const bulkCorrectionSummary = (outcome: BulkRemoveOutcome): string => {
  const s = outcome.succeeded.length;
  const f = outcome.failed.length;
  const parts: string[] = [];
  if (s > 0) {
    parts.push(`Fixed ${s} ${s === 1 ? "account" : "accounts"}.`);
  }
  if (f > 0) {
    parts.push(`${f} ${f === 1 ? "account" : "accounts"} couldn't be updated.`);
  }
  return parts.join(" ") || "No accounts were updated.";
};

/* ------------------------------------------------------------------ *
 * Home strip aggregation (PLO-4816)
 *
 * The portal Home AccessReviewsCard carries a compact strip of two attention
 * counts across the reviewer's active campaigns:
 *   - overdue:  campaigns past their due date and not complete.
 *   - answered queries needing action: accounts whose query the IAM team
 *     answered and that are back in requires_review.
 * Each strip entry links into a specific campaign, so the aggregation keeps the
 * per-campaign breakdown, not just totals.
 * ------------------------------------------------------------------ */

export interface CampaignQuerySignal {
  campaignId: string;
  /** answered queries awaiting the reviewer across this campaign */
  answeredQueries: number;
}

export interface HomeStripCampaign {
  id: string;
  status: MFCampaign["status"];
  dueDate: string;
}

export interface HomeStripEntry {
  campaignId: string;
  kind: "overdue" | "answered_queries";
  count: number;
}

export interface HomeStripModel {
  overdueCount: number;
  answeredQueryCount: number;
  entries: HomeStripEntry[];
}

/**
 * Build the Home strip model from the reviewer's campaigns + a per-campaign
 * answered-query signal. Overdue is derived from the due date on a still-open
 * campaign (reusing `dueUrgency`). Answered-query entries come from the signal
 * map. Entries are only emitted for non-zero counts, overdue first, then by
 * campaign order — so an empty strip yields an empty `entries` list and the
 * card can self-hide.
 */
export const buildHomeStrip = (
  campaigns: HomeStripCampaign[],
  querySignals: CampaignQuerySignal[] = [],
  now: Date = new Date(),
): HomeStripModel => {
  const answeredById = new Map(
    querySignals.map((s) => [s.campaignId, s.answeredQueries]),
  );

  const overdue: HomeStripEntry[] = [];
  const answered: HomeStripEntry[] = [];

  for (const c of campaigns) {
    if (dueUrgency(c.dueDate, c.status, now) === "overdue") {
      overdue.push({ campaignId: c.id, kind: "overdue", count: 1 });
    }
    const n = answeredById.get(c.id) ?? 0;
    if (n > 0) {
      answered.push({ campaignId: c.id, kind: "answered_queries", count: n });
    }
  }

  return {
    overdueCount: overdue.length,
    answeredQueryCount: answered.reduce((sum, e) => sum + e.count, 0),
    entries: [...overdue, ...answered],
  };
};

/* ------------------------------------------------------------------ *
 * Last-activity staleness (PLO-4815)
 *
 * The review-account rows carry the snapshot last-accessed date. We band it
 * so the row can emphasise access that has gone unused for a long time —
 * a strong signal that it may no longer be needed.
 * ------------------------------------------------------------------ */

/** An account is "stale" once it has gone unused for at least this many days. */
export const STALE_ACTIVITY_DAYS = 90;

/**
 * The BE sends the snapshot last-accessed date either as a bare ISO string or
 * wrapped as `{ date }` (the shape varies by serializer path). Normalise both,
 * plus null/empty, to a single `string | null`.
 */
export const normaliseSnapshotDate = (
  value: string | { date: string | null } | null | undefined,
): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() === "" ? null : value;
  return value.date && value.date.trim() !== "" ? value.date : null;
};

/**
 * Resolve an account's effective last-accessed date, preferring the explicit
 * snapshot field and falling back to `lastUsageDate` for older payloads.
 *
 * The fall-through must happen on the *normalised* value: the snapshot field is
 * often the truthy empty wrapper `{ date: null }`, so a plain `?? lastUsageDate`
 * never falls through (the wrapper isn't nullish) and the real usage date is
 * lost. Normalise each side first, then coalesce.
 */
export const resolveLastAccessed = (
  snapshotLastAccessedAt: string | { date: string | null } | null | undefined,
  lastUsageDate: string | { date: string | null } | null | undefined,
): string | null =>
  normaliseSnapshotDate(snapshotLastAccessedAt) ??
  normaliseSnapshotDate(lastUsageDate);

export type ActivityBand = "none" | "stale" | "recent";

/**
 * Band an account's last-activity date. `"none"` when there is no date at all,
 * `"stale"` once it is `STALE_ACTIVITY_DAYS`+ old, else `"recent"`. A future or
 * unparseable date is treated as `"recent"` (never falsely flagged stale).
 */
export const activityBand = (
  lastAccessed: string | { date: string | null } | null | undefined,
  now: Date = new Date(),
  thresholdDays: number = STALE_ACTIVITY_DAYS,
): ActivityBand => {
  const iso = normaliseSnapshotDate(lastAccessed);
  if (!iso) return "none";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "none";
  const ageDays = Math.floor(
    (now.getTime() - when.getTime()) / (1000 * 60 * 60 * 24),
  );
  return ageDays >= thresholdDays ? "stale" : "recent";
};

/** True when the last-activity date is stale enough to emphasise in the row. */
export const activityIsStale = (
  lastAccessed: string | { date: string | null } | null | undefined,
  now: Date = new Date(),
  thresholdDays: number = STALE_ACTIVITY_DAYS,
): boolean => activityBand(lastAccessed, now, thresholdDays) === "stale";

/**
 * Compact chip label for a row's last-activity cell. Stale accounts get an
 * explicit "No activity 90d+" (using the configured threshold) so a reviewer
 * can spot unused access at a glance; others just show "Active".
 */
export const activityChipLabel = (
  lastAccessed: string | { date: string | null } | null | undefined,
  now: Date = new Date(),
  thresholdDays: number = STALE_ACTIVITY_DAYS,
): string => {
  switch (activityBand(lastAccessed, now, thresholdDays)) {
    case "stale":
      return `No activity ${thresholdDays}d+`;
    case "recent":
      return "Active";
    default:
      return "No activity data";
  }
};

/**
 * The Activity cell's words.
 *
 * An indirect row has no activity to report: last-access is recorded against
 * an identity's own account and the row's leg is a resource-to-resource edge.
 * "No activity data" there reads as a finding about the person, which is
 * exactly the wrong conclusion for a reviewer to draw, so the cell says the
 * measurement does not exist instead.
 */
export const activityUnavailableLabel = (
  account: Pick<MFReviewAccount, "accessKind" | "activityAvailable">,
): string | null =>
  isIndirectAccount(account) || account.activityAvailable === false
    ? "Not available"
    : null;

/**
 * Whether the reviewer may correct this row's entitlements.
 *
 * An indirect row's entitlements are granted on hops that need not belong to
 * the reviewed resource, so the server rejects a correction on one with a user
 * error. Offering the control and then failing is worse than not offering it.
 */
export const canCorrectEntitlements = (
  account: Pick<MFReviewAccount, "accessKind">,
): boolean => !isIndirectAccount(account);

/* ------------------------------------------------------------------ *
 * Peer comparison display (PLO-4815)
 *
 * "X of Y peers hold this entitlement", computed BE-side over the review's
 * snapshot data. We only show it once the peer group is big enough to be
 * meaningful — a group of one or two says nothing.
 * ------------------------------------------------------------------ */

/** Hide peer comparison when the peer group is smaller than this. */
export const PEER_DISPLAY_FLOOR = 3;

export interface PeerComparison {
  holders: number;
  peerGroupSize: number;
}

/**
 * Whether a peer comparison is worth showing: it exists and its group meets
 * the floor. Below the floor the sample is too small to be informative.
 */
export const shouldShowPeerComparison = (
  peer: PeerComparison | null | undefined,
  floor: number = PEER_DISPLAY_FLOOR,
): peer is PeerComparison =>
  !!peer &&
  Number.isFinite(peer.peerGroupSize) &&
  peer.peerGroupSize >= floor &&
  Number.isFinite(peer.holders);

/**
 * Compact per-entitlement label, e.g. `"3/12 peers"`. Returns null when the
 * comparison is below the display floor (caller renders nothing).
 */
export const peerCompactLabel = (
  peer: PeerComparison | null | undefined,
  floor: number = PEER_DISPLAY_FLOOR,
): string | null => {
  if (!shouldShowPeerComparison(peer, floor)) return null;
  return `${peer.holders}/${peer.peerGroupSize} peers`;
};

/**
 * Fuller sentence for the drawer, e.g.
 * `"3 of 12 peers in Engineering also hold Admin."`. `groupLabel` is the
 * department/job-title label (nullable); when absent it degrades to
 * "…peers also hold Admin.". Returns null below the display floor.
 */
export const peerSentence = (
  entitlementName: string,
  peer: PeerComparison | null | undefined,
  groupLabel?: string | null,
  floor: number = PEER_DISPLAY_FLOOR,
): string | null => {
  if (!shouldShowPeerComparison(peer, floor)) return null;
  const where =
    groupLabel && groupLabel.trim() !== "" ? ` in ${groupLabel}` : "";
  const peersWord = peer.peerGroupSize === 1 ? "peer" : "peers";
  return `${peer.holders} of ${peer.peerGroupSize} ${peersWord}${where} also hold ${entitlementName}.`;
};

/* ------------------------------------------------------------------ *
 * Review deadline extension requests (PLO-4856)
 *
 * A reviewer working through an access review can ask for more time before
 * their set's review deadline. The server carries the rules + current state on
 * the review-details payload's `deadlineExtension` wrapper (null when the cycle
 * has no rules, rules are disabled, or the viewer is an attestor). These pure
 * functions decide whether the action is offered, validate a request, describe
 * the allowance, derive the banner state, and resolve the effective due date —
 * so the modal/banner/button components stay presentational.
 * ------------------------------------------------------------------ */

/**
 * Whether the "Request more time" action should render. Requires a non-null,
 * enabled wrapper with at least one request remaining, remaining extension days
 * available, no request already pending, and a non-read-only (reviewer) view.
 * Attestors never see it — the server already omits the wrapper for them, but
 * we gate on `viewMode` too so the button can never leak into a read-only
 * drill-in.
 *
 * `remainingTotalDays` is the server's live allowance (`max(cap - used, 0)`,
 * never "unlimited"); at 0 the server rejects every request, so the action must
 * not be offered.
 */
export const canRequestDeadlineExtension = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
  viewMode?: MFReviewDetails["viewMode"],
): boolean => {
  if (viewMode === "attestor") return false;
  const ext = deadlineExtension;
  if (!ext || !ext.enabled) return false;
  if (ext.pendingRequest != null) return false;
  if (typeof ext.remainingTotalDays === "number" && ext.remainingTotalDays <= 0)
    return false;
  return ext.remainingRequests > 0;
};

/**
 * The largest number of days a reviewer may request right now. The server
 * always surfaces `remainingTotalDays` as the live cap (`max(cap - used, 0)`) —
 * there is no "unlimited" mode — so this is that value (0 = allowance
 * exhausted, nothing requestable). Null only when the wrapper/field is missing
 * (older payload), meaning the cap is unknown rather than absent.
 */
export const maxRequestableExtensionDays = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
): number | null => {
  if (!deadlineExtension) return null;
  const remaining = deadlineExtension.remainingTotalDays;
  return typeof remaining === "number" ? Math.max(remaining, 0) : null;
};

/**
 * Would a request for `days` auto-grant instantly (vs. queue for admin)? True
 * only when the cycle sets a positive auto-grant threshold and the request is
 * at or under BOTH the threshold and the remaining total allowance (mirrors the
 * server's `Allowance.auto_grant?`). A null/0 threshold means every request
 * queues → always false.
 */
export const extensionAutoGrants = (
  days: number,
  deadlineExtension: MFDeadlineExtension | null | undefined,
): boolean => {
  const threshold = deadlineExtension?.autoGrantMaxDays;
  if (typeof threshold !== "number" || threshold <= 0) return false;
  const max = maxRequestableExtensionDays(deadlineExtension);
  if (max != null && days > max) return false;
  return Number.isFinite(days) && days >= 1 && days <= threshold;
};

/**
 * Helper text for the days input: the allowance ("You can request up to N more
 * days") plus, when the cycle auto-grants small requests, the instant-grant
 * threshold. Null when there is no wrapper, and null when the allowance is
 * exhausted (max = 0) — nothing can be requested, so no helper text applies
 * (the action is gated off by canRequestDeadlineExtension anyway).
 */
export const deadlineExtensionAllowanceText = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
): string | null => {
  if (!deadlineExtension) return null;
  const max = maxRequestableExtensionDays(deadlineExtension);
  if (max === 0) return null;
  const parts: string[] = [];
  if (max != null) {
    parts.push(
      `You can request up to ${max} more ${max === 1 ? "day" : "days"}.`,
    );
  }
  const threshold = deadlineExtension.autoGrantMaxDays;
  if (typeof threshold === "number" && threshold > 0) {
    parts.push(
      `Requests of ${threshold} ${threshold === 1 ? "day" : "days"} or fewer are granted instantly.`,
    );
  }
  return parts.length > 0 ? parts.join(" ") : null;
};

export interface ExtensionRequestValidation {
  valid: boolean;
  /** field-scoped error, or null when that field is fine */
  daysError: string | null;
  reasonError: string | null;
}

/**
 * Validate a pending extension request against the cycle rules. `days` must be
 * a whole number ≥ 1 and, when the remaining-total cap is known, ≤ that cap
 * (a cap of 0 means the allowance is exhausted — every day count fails).
 * `reason` is required (non-blank) iff `requireReason`. Returns per-field errors
 * so the modal can place them inline; `valid` is true only when both are clear.
 */
export const validateExtensionRequest = (
  days: number,
  reason: string,
  deadlineExtension: MFDeadlineExtension | null | undefined,
): ExtensionRequestValidation => {
  let daysError: string | null = null;
  let reasonError: string | null = null;

  const max = maxRequestableExtensionDays(deadlineExtension);
  if (max === 0) {
    daysError = "No further extension is available for this review.";
  } else if (!Number.isFinite(days) || !Number.isInteger(days) || days < 1) {
    daysError = "Enter a whole number of days (at least 1).";
  } else if (max != null && days > max) {
    daysError = `You can request at most ${max} more ${max === 1 ? "day" : "days"}.`;
  }

  if (deadlineExtension?.requireReason && reason.trim() === "") {
    reasonError = "A reason is required for this request.";
  }

  return {
    valid: daysError === null && reasonError === null,
    daysError,
    reasonError,
  };
};

/**
 * The effective review due date to DISPLAY: prefer the extension wrapper's
 * `effectiveDueDate` (override || cycle due date, server-computed), falling
 * through to the campaign's cycle due date when there is no wrapper.
 *
 * Both inputs are normalised through `normaliseSnapshotDate` first: server date
 * fields sometimes arrive as a `{ date: string | null }` wrapper rather than a
 * bare string, and a truthy-but-empty `{ date: null }` wrapper must fall
 * through to the campaign date (a plain `??` never would). Returns null when
 * neither yields a usable date.
 */
export const effectiveReviewDueDate = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
  campaignDueDate: string | { date: string | null } | null | undefined,
): string | null =>
  normaliseSnapshotDate(deadlineExtension?.effectiveDueDate) ??
  normaliseSnapshotDate(campaignDueDate);

/**
 * The review-page banner state for the deadline-extension wrapper:
 *   - "pending": a request is awaiting an admin decision (action disabled).
 *   - "denied":  the most recent request was denied and none is pending (the
 *                reviewer may re-request if they still have allowance).
 *   - "granted": an override has been applied (the due date now reflects it)
 *                and there is no pending/denied signal to show instead.
 *   - null:      nothing to surface.
 * Pending takes priority over a stale denial; a denial takes priority over the
 * quiet "granted" state so the reviewer always sees the most actionable signal.
 */
export type DeadlineExtensionBannerState =
  "pending" | "denied" | "granted" | null;

const isNonBlank = (v: string | null | undefined): v is string =>
  typeof v === "string" && v.trim().length > 0;

export const deadlineExtensionBannerState = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
): DeadlineExtensionBannerState => {
  const ext = deadlineExtension;
  if (!ext) return null;
  if (ext.pendingRequest != null) return "pending";
  if (isNonBlank(ext.deniedReason)) return "denied";
  if (isNonBlank(ext.dueDateOverride)) return "granted";
  return null;
};

/**
 * Banner copy for a pending request, e.g.
 * "Extension requested (5 days): awaiting approval". Null when there is no
 * pending request.
 */
export const pendingExtensionBannerText = (
  deadlineExtension: MFDeadlineExtension | null | undefined,
): string | null => {
  const pending = deadlineExtension?.pendingRequest;
  if (!pending) return null;
  const days = pending.requestedDays;
  return `Extension requested (${days} ${days === 1 ? "day" : "days"}): awaiting approval`;
};

/* ------------------------------------------------------------------ *
 * Row fidelity — identity provider, entitlement chip parts, overflow fit
 * (PLO-4803 P3)
 *
 * The account row's identity cell shows the login PROVIDER as a branded tile +
 * display name (design.tsx IdentityAvatar/IdentityCell). The provider is
 * derived client-side from the identity's `auth_type` (falling back to the
 * resource type), mapped to a small set of canonical keys the view has brand
 * glyphs for. An unrecognised provider yields a null key so the view renders
 * the generic Shield tile with a sentence-cased name.
 * ------------------------------------------------------------------ */

/** Providers the row renders a branded logo tile for. Anything else → Shield. */
export type IdentityProviderKey =
  | "google"
  | "microsoft"
  | "okta"
  | "github"
  | "gitlab"
  | "slack"
  | "aws"
  | "auth0";

export interface IdentityProviderInfo {
  /** canonical key for the brand visual, or null → generic (Shield) tile. */
  key: IdentityProviderKey | null;
  /** display name for the cell, e.g. "Google Workspace". */
  name: string;
}

interface ProviderEntry {
  key: IdentityProviderKey;
  name: string;
  /** normalised alias tokens that resolve to this provider. */
  aliases: string[];
}

const PROVIDER_REGISTRY: ProviderEntry[] = [
  {
    key: "google",
    name: "Google Workspace",
    aliases: ["google", "googleworkspace", "gsuite", "gapps"],
  },
  {
    key: "microsoft",
    name: "Microsoft Entra",
    aliases: [
      "microsoft",
      "azure",
      "azuread",
      "entra",
      "office365",
      "o365",
      "outlook",
      "msgraph",
    ],
  },
  { key: "okta", name: "Okta", aliases: ["okta"] },
  { key: "github", name: "GitHub", aliases: ["github", "githubenterprise"] },
  { key: "gitlab", name: "GitLab", aliases: ["gitlab"] },
  { key: "slack", name: "Slack", aliases: ["slack"] },
  { key: "aws", name: "AWS", aliases: ["aws", "amazon", "amazonaws", "iam"] },
  { key: "auth0", name: "Auth0", aliases: ["auth0"] },
];

/** Strip to lowercase alphanumerics so "Azure AD" / "azure_ad" both match "azuread". */
const normaliseProviderToken = (raw: string | null | undefined): string =>
  (raw ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const sentenceCaseProvider = (raw: string | null | undefined): string => {
  const text = (raw ?? "").replace(/[_-]+/g, " ").trim();
  if (!text) return "Unknown provider";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * The identity provider for a review-account row. Resolves the branded key from
 * the identity's `auth_type`, falling back to the resource type when the
 * identity carries nothing recognisable. An unmatched value yields
 * `{ key: null, name: sentence-cased raw }` so the view keeps the generic tile
 * but still labels the provider.
 */
export const identityProvider = (account: {
  identity?: { auth_type?: string | null } | null;
  resource?: { type?: string | null } | null;
}): IdentityProviderInfo => {
  const authType = account.identity?.auth_type;
  const resourceType = account.resource?.type;

  for (const source of [authType, resourceType]) {
    const token = normaliseProviderToken(source);
    if (!token) continue;
    const match = PROVIDER_REGISTRY.find((p) =>
      p.aliases.some((a) => token === a || token.includes(a)),
    );
    if (match) return { key: match.key, name: match.name };
  }

  // Nothing recognisable — generic tile, best-effort readable name.
  return { key: null, name: sentenceCaseProvider(authType ?? resourceType) };
};

/**
 * The two rendered parts of an entitlement chip: the category/type PREFIX
 * (styled as a label, e.g. "Repositories") and the VALUE (the entitlement
 * name). The type comes from the snapshot; when it is blank there is no prefix
 * and the chip shows just the value. Mirrors the design's `mode="type"`
 * EntitlementTag, but keyed off real snapshot data rather than name-prefix
 * inference.
 */
export interface EntitlementChipParts {
  type: string | null;
  value: string;
}

export const entitlementChipParts = (entitlement: {
  snapshotEntitlementName: string;
  snapshotEntitlementType?: string | null;
}): EntitlementChipParts => {
  const type = entitlement.snapshotEntitlementType?.trim();
  return {
    type: type ? type : null,
    value: entitlement.snapshotEntitlementName,
  };
};

/**
 * Normalise a free-text description (an entitlement's, a resource's) for
 * display: trimmed text, or null when there is nothing renderable. Same
 * contract as `applicationInstructionText`; kept separate so the two surfaces
 * can diverge without one silently changing the other.
 */
export const descriptionText = (
  description: string | null | undefined,
): string | null => {
  const trimmed = description?.trim();
  return trimmed ? trimmed : null;
};

/**
 * The hover label for an entitlement chip (PLO-5211). Line one is the typed
 * name plus the peer-comparison sentence when there is one; line two is the
 * entitlement's description when it has one. Rendered with pre-line so the
 * newline survives.
 */
export const entitlementTooltipLabel = ({
  name,
  type,
  description,
  peerLabel,
}: {
  name: string;
  type?: string | null;
  description?: string | null;
  peerLabel?: string | null;
}): string => {
  const headline = [
    type ? `${type}: ${name}` : name,
    peerLabel ? `${peerLabel} in this department also hold it` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const body = descriptionText(description);
  return body ? `${headline}\n${body}` : headline;
};

/**
 * How many chips fit in a container of `containerWidth`, reserving room for a
 * trailing "+N" overflow marker whenever anything is hidden. `chipWidths` are
 * the measured full widths (px) in render order. Pure port of the design's
 * OverflowChips recompute loop so the fit maths is unit-testable without a DOM.
 *
 * At least one chip always shows when any exist (even if it overflows) — the
 * row never collapses to a bare "+N".
 */
export const fitChips = (
  chipWidths: number[],
  containerWidth: number,
  opts: { gap?: number; moreWidth?: number } = {},
): number => {
  const n = chipWidths.length;
  if (n === 0) return 0;
  const gap = opts.gap ?? 4;
  const more = opts.moreWidth ?? 34;

  let used = 0;
  let fit = 0;
  for (let i = 0; i < n; i++) {
    const w = chipWidths[i] + (i > 0 ? gap : 0);
    const hasMoreAfter = i < n - 1; // chips remain → keep room for the +N marker
    if (used + w + (hasMoreAfter ? gap + more : 0) <= containerWidth) {
      used += w;
      fit++;
    } else {
      break;
    }
  }
  return Math.max(1, fit);
};

/* ------------------------------------------------------------------ *
 * Fix-incorrect-data corrections (PLO-4803 P5)
 *
 * "Fixing" an account's recorded access is a FULL-REPLACE of its entitlement
 * set: the reviewer ticks the entitlements the account actually has at source,
 * and the whole set is persisted (POST .../accounts/{id}/entitlements). The
 * corrections stage as DRAFTS first (per the design's CorrectionModal) and only
 * persist when saved from the corrections panel. These pure helpers derive the
 * add/remove diff (for the "Adding"/"Removing" chips + draft cards), the persist
 * payload, the grouped catalogue, and the bulk tri-state maths — so the modals
 * and panel stay presentational.
 * ------------------------------------------------------------------ */

/**
 * An entitlement correction for one account being applied: the desired
 * full-replace set + names, plus the resolved added/removed names. Corrections
 * auto-save (applied immediately); once persisted they come back on the account
 * payload's `entitlementCorrections` field, which is what the panel renders.
 */
export interface StagedCorrection {
  accountId: string;
  accountLabel: string;
  /** desired full-replace set of existing entitlement ids. */
  entitlementIds: string[];
  /** free-text entitlements to create + attach. */
  newEntitlementNames: string[];
  /** display names added by this correction (existing + new). */
  addedNames: string[];
  /** display names removed by this correction. */
  removedNames: string[];
}

export interface EntitlementDiff {
  /** entitlement ids present in the desired set but not the original. */
  added: string[];
  /** entitlement ids present in the original set but not the desired. */
  removed: string[];
}

/**
 * The add/remove diff between an account's original entitlement id set and the
 * reviewer's desired set. Order follows the desired/original inputs; ids are
 * de-duplicated. Drives the draft correction card + the "Adding"/"Removing"
 * row chips.
 */
export const deriveEntitlementDiff = (
  originalIds: string[],
  desiredIds: string[],
): EntitlementDiff => {
  const original = new Set(originalIds);
  const desired = new Set(desiredIds);
  return {
    added: [...desired].filter((id) => !original.has(id)),
    removed: [...original].filter((id) => !desired.has(id)),
  };
};

/** Whether a desired set actually differs from the original (gates Apply). */
export const hasEntitlementChange = (
  originalIds: string[],
  desiredIds: string[],
  newEntitlementNames: string[] = [],
): boolean => {
  if (newEntitlementNames.length > 0) return true;
  const diff = deriveEntitlementDiff(originalIds, desiredIds);
  return diff.added.length > 0 || diff.removed.length > 0;
};

export interface EntitlementCorrectionPayload {
  accountId: string;
  /** the full replacement set of existing entitlement ids. */
  entitlementIds: string[];
  /** free-text entitlements to create + attach (omit-safe: empty when none). */
  newEntitlementNames: string[];
}

/**
 * The full-replace persist payload for one account's correction. `desiredIds` is
 * the complete post-correction set (NOT a delta) — the endpoint replaces the
 * account's entitlements wholesale. New free-text entitlements are carried
 * separately so the server can create then attach them.
 */
export const correctionToPayload = (
  accountId: string,
  desiredIds: string[],
  newEntitlementNames: string[] = [],
): EntitlementCorrectionPayload => ({
  accountId,
  entitlementIds: [...new Set(desiredIds)],
  newEntitlementNames: [
    ...new Set(newEntitlementNames.map((n) => n.trim()).filter(Boolean)),
  ],
});

export interface OptionGroup<T> {
  category: string;
  items: T[];
}

/**
 * Group entitlement options by their `type`, bucketing blank/absent types under
 * a trailing "Other" group. Known-type groups sort alphabetically by category;
 * "Other" always sorts last. Items within a group sort by name. Mirrors the
 * design's grouped correction checklist.
 */
export const groupOptionsByCategory = <
  T extends { name: string; type?: string | null },
>(
  options: T[],
  otherLabel = "Other",
): OptionGroup<T>[] => {
  const byCategory = new Map<string, T[]>();
  for (const opt of options) {
    const type = opt.type?.trim();
    const category = type ? type : otherLabel;
    const bucket = byCategory.get(category) ?? [];
    bucket.push(opt);
    byCategory.set(category, bucket);
  }

  return [...byCategory.entries()]
    .sort(([a], [b]) => {
      if (a === otherLabel) return 1;
      if (b === otherLabel) return -1;
      return a.localeCompare(b);
    })
    .map(([category, items]) => ({
      category,
      items: [...items].sort((x, y) => x.name.localeCompare(y.name)),
    }));
};

/* --- bulk tri-state (design BulkCorrectionModal) ------------------------ *
 * Across a selection, each catalogue entitlement starts checked (every account
 * has it), unchecked (none do) or mixed (some do). Ticking checked → add to all,
 * unchecked → remove from all, left mixed → leave each account as-is. */

export type EntState = "checked" | "unchecked" | "mixed";

export interface AccountEntitlements {
  accountId: string;
  entitlementIds: string[];
}

/**
 * The initial tri-state for every catalogue id across a selection: `checked`
 * when all selected accounts hold it, `unchecked` when none do, else `mixed`.
 */
export const bulkEntitlementStates = (
  selections: AccountEntitlements[],
  catalogueIds: string[],
): Record<string, EntState> => {
  const count = selections.length;
  const out: Record<string, EntState> = {};
  for (const id of catalogueIds) {
    const have = selections.filter((s) => s.entitlementIds.includes(id)).length;
    out[id] = have === 0 ? "unchecked" : have === count ? "checked" : "mixed";
  }
  return out;
};

/**
 * The next tri-state when a bulk row is toggled. An entitlement that STARTED
 * mixed cycles checked → unchecked → mixed (mixed = "leave as-is" is a real
 * third stop); a uniform entitlement just flips checked ↔ unchecked.
 */
export const nextBulkEntState = (
  originalState: EntState,
  currentState: EntState,
): EntState => {
  if (originalState === "mixed") {
    if (currentState === "mixed") return "checked";
    if (currentState === "checked") return "unchecked";
    return "mixed";
  }
  return currentState === "checked" ? "unchecked" : "checked";
};

/**
 * Apply the chosen bulk tri-state to each account: `checked` adds the id,
 * `unchecked` removes it, `mixed` leaves that account's membership untouched —
 * so a partially-held entitlement left mixed keeps every account exactly as it
 * was (this is what fixes the old lossy intersection-replace). Returns the new
 * full entitlement set per account.
 */
export const applyBulkStates = (
  selections: AccountEntitlements[],
  state: Record<string, EntState>,
): AccountEntitlements[] =>
  selections.map(({ accountId, entitlementIds }) => {
    const set = new Set(entitlementIds);
    for (const [id, st] of Object.entries(state)) {
      if (st === "checked") set.add(id);
      else if (st === "unchecked") set.delete(id);
      // mixed → leave this account's membership as-is
    }
    return { accountId, entitlementIds: [...set] };
  });

/** Whether the bulk state differs from its origin (gates the bulk Apply). */
export const bulkStateChanged = (
  originalState: Record<string, EntState>,
  state: Record<string, EntState>,
): boolean =>
  Object.keys({ ...originalState, ...state }).some(
    (id) => originalState[id] !== state[id],
  );

/* ------------------------------------------------------------------ *
 * Luna suggestion buckets (PLO-5182, reworded PLO-5574)
 *
 * A review outcome is always the reviewer's call, so a bucket label may only
 * state what Luna suggests, never that an outcome has been decided. Internal
 * audit settled on "suggestion" over "recommendation" for exactly that reason.
 *
 * The keys are URL-visible (`?subtab=`), so they stay as they are and only
 * the human-facing label changes; existing deep links keep working.
 * ------------------------------------------------------------------ */

export type SuggestionBucketKey =
  "auto-approve" | "auto-reject" | "needs-review";

export const SUGGESTION_BUCKET_LABEL: Record<SuggestionBucketKey, string> = {
  "auto-approve": "Approval suggested",
  "auto-reject": "Revocation suggested",
  "needs-review": "Manual review suggested",
};

/* ------------------------------------------------------------------ *
 * One row per person (PLO-5424)
 *
 * The grouped accounts endpoint folds a person's duplicate identities (e.g.
 * an SSO identity AND an email-password identity) into ONE row carrying every
 * identity plus the underlying flat account rows. The table keeps operating
 * on MFReviewAccount, so each grouped row is synthesised into a person row:
 * the PRIMARY account's row, overlaid with the person-level member,
 * entitlement union and derived review state, plus the person extras below.
 * The row's id stays the primary account id, so selection, the drawer, the
 * stage partition and the virtualised table all keep working unchanged.
 * Decisions expand back to real account ids at the mutation boundary.
 * ------------------------------------------------------------------ */

export interface PersonRowExtras {
  /** Every underlying account id, primary first. */
  personAccountIds: string[];
  /** Every identity the person holds in this review. */
  personIdentities: MFPersonIdentity[];
  /** The underlying flat account rows, one per identity. */
  personAccounts: MFReviewAccount[];
}

export type MFPersonReviewRow = MFReviewAccount & PersonRowExtras;

/**
 * The person-level review outcome. A person shows PENDING until every one of
 * their accounts is decided (a partially-decided person must stay actionable
 * for the remaining identities). Once all are decided: a uniform outcome
 * shows as itself; a mixed one shows the primary account's outcome. A parked
 * query on the primary surfaces while undecided so the query chip renders.
 */
export const derivePersonReview = (
  accounts: ReadonlyArray<Pick<MFReviewAccount, "review">>,
  primary: Pick<MFReviewAccount, "review">,
): MFReviewAccount["review"] => {
  if (accounts.length === 0) return primary.review ?? null;
  const allDecided = accounts.every((a) => isReviewed(a.review));
  if (!allDecided) {
    // Pending overall — but keep a parked query visible (it is not a decision).
    return primary.review === "query" ? "query" : null;
  }
  const first = accounts[0].review ?? null;
  const uniform = accounts.every((a) => (a.review ?? null) === first);
  return uniform ? first : (primary.review ?? null);
};

/**
 * The person-level status. Uniform statuses pass through; while any account
 * still requires review the person does too; otherwise the primary's status
 * stands in for the mix.
 */
export const derivePersonStatus = (
  accounts: ReadonlyArray<Pick<MFReviewAccount, "status">>,
  primary: Pick<MFReviewAccount, "status">,
): MFReviewAccount["status"] => {
  if (accounts.length === 0) return primary.status;
  const first = accounts[0].status;
  if (accounts.every((a) => a.status === first)) return first;
  if (accounts.some((a) => a.status === "requires_review")) {
    return "requires_review";
  }
  return primary.status;
};

/**
 * Synthesise one table row from a grouped person row. Null when the grouped
 * row carries no underlying accounts (nothing to act on). The primary account
 * is matched by primaryAccountId, falling back to the first account.
 */
export const personRowFromGrouped = (
  row: MFGroupedPersonRow,
): MFPersonReviewRow | null => {
  const accounts = row.accounts ?? [];
  if (accounts.length === 0) return null;
  const primary =
    accounts.find((a) => a.id === row.primaryAccountId) ?? accounts[0];

  return {
    ...primary,
    // Orphan/NHI person rows arrive with member: null — keep the primary's
    // member object (its fields are null), matching the flat row shape.
    member: row.member ?? primary.member,
    review: derivePersonReview(accounts, primary),
    status: derivePersonStatus(accounts, primary),
    stage: row.stage ?? primary.stage,
    entitlements: row.entitlements ?? primary.entitlements,
    entitlementCount: row.entitlementCount ?? primary.entitlementCount,
    flaggedCount: row.flaggedCount ?? primary.flaggedCount,
    personAccountIds:
      row.accountIds && row.accountIds.length > 0
        ? row.accountIds
        : accounts.map((a) => a.id),
    personIdentities: row.identities ?? [],
    personAccounts: accounts,
  };
};

const ACCESS_KINDS = ["direct", "indirect"] as const;

/**
 * The entitlement union across a set of accounts, keyed and sorted by
 * entitlement name like the server's grouping. For a name held by several
 * accounts a flagged snapshot wins, so the union's flagged count never hides a
 * flag raised on a non-primary identity.
 */
const unionEntitlements = (
  accounts: ReadonlyArray<MFReviewAccount>,
): MFAccessReviewAccountEntitlement[] => {
  const byName = new Map<string, MFAccessReviewAccountEntitlement>();
  accounts.forEach((account) => {
    (account.entitlements ?? []).forEach((entitlement) => {
      const seen = byName.get(entitlement.snapshotEntitlementName);
      if (
        !seen ||
        (!seen.flaggedForAdjustment && entitlement.flaggedForAdjustment)
      ) {
        byName.set(entitlement.snapshotEntitlementName, entitlement);
      }
    });
  });
  // Codepoint order, not locale order: the server sorts the unsplit row's
  // entitlements with Enum.sort_by, so localeCompare would reorder mixed-case
  // names against every row that was never split.
  return [...byName.values()].sort((a, b) => {
    const left = a.snapshotEntitlementName;
    const right = b.snapshotEntitlementName;
    return left < right ? -1 : left > right ? 1 : 0;
  });
};

/**
 * Split a grouped person row into one row per access kind.
 *
 * The grouped endpoint folds a person's identities into one row on the
 * assumption of one account per identity. An identity can now hold BOTH a
 * direct and an indirect row on the same resource, and each is its own
 * attestation with its own remediation — folding them would hide one of them
 * behind the other's decision. A person holding only one kind is returned
 * unchanged (same reference), so nothing re-renders for the common case.
 *
 * The person-level entitlement union spans every identity AND both kinds, so
 * each partition recomputes its own union over the accounts it keeps.
 */
export const splitGroupedByAccessKind = (
  row: MFGroupedPersonRow,
): MFGroupedPersonRow[] => {
  const accounts = row.accounts ?? [];
  const present = ACCESS_KINDS.filter((kind) =>
    accounts.some((account) => accessKindOf(account) === kind),
  );
  if (present.length <= 1) return [row];

  return present.map((kind) => {
    const kept = accounts.filter((account) => accessKindOf(account) === kind);
    const keptIds = new Set(kept.map((account) => account.id));
    const entitlements = unionEntitlements(kept);
    const holdsPrimary = keptIds.has(row.primaryAccountId);
    const primary = holdsPrimary ? row.primaryAccountId : kept[0].id;
    const ids = kept
      .map((account) => account.id)
      .filter((id) => (row.accountIds ?? []).includes(id));

    return {
      ...row,
      personKey: `${row.personKey}:${kind}`,
      accounts: kept,
      // Primary first, as `personAccountIds` promises. Neither `row.accountIds`
      // nor `row.accounts` is ordered around THIS partition's primary, so
      // hoisting it is the only thing that holds the invariant.
      accountIds: [primary, ...ids.filter((id) => id !== primary)],
      identities: (row.identities ?? []).filter((identity) =>
        keptIds.has(identity.accountId),
      ),
      primaryAccountId: primary,
      entitlements,
      entitlementCount: entitlements.length,
      flaggedCount: entitlements.filter((e) => e.flaggedForAdjustment).length,
    };
  });
};

/** Synthesise the whole grouped listing, dropping empty rows. */
export const personRowsFromGrouped = (
  rows: MFGroupedPersonRow[],
): MFPersonReviewRow[] =>
  rows
    .flatMap(splitGroupedByAccessKind)
    .map(personRowFromGrouped)
    .filter((row): row is MFPersonReviewRow => row !== null);

/**
 * The account ids a decision on this person targets: every underlying account
 * that is still undecided, optionally restricted to `scope` (the card-scoped
 * table passes its filterAccountIds so a decision inside an AI-recommendation
 * card never reaches identities outside that card). An EMPTY scope means "no
 * restriction", matching the row filter — a scope of `[]` narrows nothing on
 * display, so it must not silently narrow every decision to nothing either.
 */
export const personUndecidedAccountIds = (
  row: MFPersonReviewRow,
  scope?: readonly string[],
): string[] => {
  const restricted = scope !== undefined && scope.length > 0;
  return row.personAccounts
    .filter((a) => !isReviewed(a.review))
    .map((a) => a.id)
    .filter((id) => !restricted || scope.includes(id));
};

/** Whether an identity's recorded outcome has frozen it (see `isAccountDecisionFrozen`). */
export type FrozenAccountPredicate = (
  account: MFPersonReviewRow["personAccounts"][number],
) => boolean;

/**
 * The account ids a single-row decision (or query) targets.
 *
 * Undecided identities come first — a decision on a person must not overwrite
 * identities already decided one by one. But once EVERY in-scope identity is
 * decided, the reviewer is changing their mind, and the decision has to reach
 * those identities or the row is stuck: there is no "clear" call, the server
 * simply accepts a fresh outcome on an already-decided account (PLO-5749).
 * An identity `isFrozen` (its outcome started de-provisioning) is never
 * re-targeted, whatever its siblings do.
 */
export const personDecisionTargetIds = (
  row: MFPersonReviewRow,
  scope?: readonly string[],
  isFrozen: FrozenAccountPredicate = () => false,
): string[] => {
  const undecided = personUndecidedAccountIds(row, scope);
  if (undecided.length > 0) return undecided;
  const restricted = scope !== undefined && scope.length > 0;
  return row.personAccounts
    .filter((a) => !isFrozen(a))
    .map((a) => a.id)
    .filter((id) => !restricted || scope.includes(id));
};

/**
 * The single account a correction targets for this person. A correction
 * full-replaces ONE account's entitlement set, so this resolves to a real
 * underlying account, never the synthesised person row (whose entitlements are
 * the person-wide union).
 *
 * Scoped for the same reason `personUndecidedAccountIds` is, and the row id is
 * exactly why: a person row appears in a card-scoped table when ANY of their
 * accounts is in the card, but the row's id is always the PRIMARY account id.
 * Resolving by row id alone therefore rewrites an identity the card never
 * showed and leaves the recommended one untouched (PLO-5575). Prefers the
 * primary when it is in scope, so the unscoped table is unchanged.
 */
export const personCorrectionAccount = (
  row: MFPersonReviewRow,
  scope?: readonly string[],
): MFReviewAccount => {
  const restricted = scope !== undefined && scope.length > 0;
  const inScope = (a: MFReviewAccount) => !restricted || scope.includes(a.id);
  return (
    row.personAccounts.find((a) => a.id === row.id && inScope(a)) ??
    row.personAccounts.find(inScope) ??
    row
  );
};

/**
 * The accounts a bulk correction targets: one real underlying account per
 * selected, still-undecided row, minus the rows the server would reject (see
 * `canCorrectEntitlements`). An empty result means the whole selection is
 * uncorrectable, so the affordance itself has nothing to open.
 */
export const bulkCorrectionAccounts = (
  rows: ReadonlyArray<MFPersonReviewRow>,
  selectedRowIds: readonly string[],
  scope?: readonly string[],
): MFReviewAccount[] =>
  rows
    .filter(
      (row) =>
        selectedRowIds.includes(row.id) &&
        !isAccountDecided(row) &&
        canCorrectEntitlements(row),
    )
    .map((row) => personCorrectionAccount(row, scope));

/** Expand selected row ids to the account ids their decisions target. */
export const expandRowIdsToAccountIds = (
  rows: ReadonlyArray<MFPersonReviewRow>,
  rowIds: readonly string[],
  scope?: readonly string[],
): string[] =>
  rows
    .filter((row) => rowIds.includes(row.id))
    .flatMap((row) => personUndecidedAccountIds(row, scope));

/**
 * Map flat account ids (AI suggestion buckets, the parent's selection echo)
 * back to the person rows containing them — deduped, in row order.
 */
export const mapAccountIdsToRowIds = (
  rows: ReadonlyArray<MFPersonReviewRow>,
  accountIds: readonly string[],
): string[] =>
  rows
    .filter((row) => row.personAccountIds.some((id) => accountIds.includes(id)))
    .map((row) => row.id);

/**
 * The suggestion for a person row: suggestion maps are keyed by flat account
 * id, so the first hit among the person's accounts wins.
 */
export const personSuggestionFor = (
  row: MFPersonReviewRow,
  suggestions: Record<string, string>,
): string | undefined => {
  for (const id of row.personAccountIds) {
    if (suggestions[id] !== undefined) return suggestions[id];
  }
  return undefined;
};

/**
 * Outcomes that take access away. Only these get the per-identity scope
 * chooser on a multi-identity person — approvals and out-of-scope always act
 * on the whole person.
 */
const REVOKE_MAPPINGS: readonly string[] = [
  "not_required",
  "non_compliant",
  "not_appropriate",
  "remove",
  "removed",
];

export const isRevokeMapping = (mapping: string): boolean =>
  REVOKE_MAPPINGS.includes(mapping);

/**
 * Whether choosing `action` on this person must first ask which identities it
 * covers: only revoke outcomes, and only when more than one identity is
 * actionable — undecided ones, or every unfrozen one on a re-decision (see
 * `personDecisionTargetIds`).
 */
export const needsRevokeScopeChoice = (
  row: MFPersonReviewRow,
  action: string,
  scope?: readonly string[],
  isFrozen?: FrozenAccountPredicate,
): boolean =>
  isRevokeMapping(action) &&
  row.personIdentities.length > 1 &&
  personDecisionTargetIds(row, scope, isFrozen).length > 1;

/** The identity rows for the popover / scope chooser, primary first. */
export const orderedPersonIdentities = (
  row: Pick<MFPersonReviewRow, "personIdentities">,
): MFPersonIdentity[] =>
  [...row.personIdentities].sort(
    (a, b) => Number(b.isPrimary ?? false) - Number(a.isPrimary ?? false),
  );

/**
 * How an identity is labelled wherever the reviewer has to tell one from
 * another: its login USERNAME first, then the external id, then the display
 * name. Username leads because a person's identities collide on everything
 * else the row shows — same member, same email, same display name — and
 * differ exactly by login ("juliet.taylor" vs "juliet.taylor_vm"), while the
 * external id is a provider-generated string ("00u17tdekrey2ciTm417") that
 * distinguishes them without telling the reviewer anything (PLO-5750). Same
 * precedence the resource-access identity column uses. Null when the identity
 * carries no usable label.
 */
export const identityDisplayValue = (identity: {
  username?: string | null;
  extId?: string | null;
  displayName?: string | null;
}): string | null => {
  const usable = (value?: string | null) => value?.trim() || null;
  return (
    usable(identity.username) ??
    usable(identity.extId) ??
    usable(identity.displayName)
  );
};

/**
 * Every identity string a row can be searched by — each identity's username
 * and external id. Spans ALL of a grouped person row's identities, not just
 * the primary: the reviewer searches for what the Identity column showed
 * them, and on a grouped row that may well be a secondary identity
 * ("juliet.taylor_vm"). The external id stays searchable so a reviewer
 * working from an audit export can still paste one in (PLO-5750).
 */
export const identitySearchTerms = (row: {
  identity?: { username?: string | null; extId?: string | null } | null;
  personIdentities?: Array<{
    username?: string | null;
    extId?: string | null;
  }> | null;
}): string[] =>
  [...(row.personIdentities ?? []), ...(row.identity ? [row.identity] : [])]
    .flatMap((identity) => [identity.username, identity.extId])
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));

/**
 * The label parts for one identity in the popover / scope chooser: the
 * provider (via the same registry the identity column uses), the auth-type
 * qualifier, and the identifying value.
 */
export interface PersonIdentityLabel {
  provider: IdentityProviderInfo;
  authTypeLabel: string | null;
  value: string;
  isPrimary: boolean;
  mfaEnabled: boolean;
}

export const personIdentityLabel = (
  identity: MFPersonIdentity,
  resource?: { type?: string | null } | null,
): PersonIdentityLabel => {
  const provider = identityProvider({
    identity: { auth_type: identity.authType },
    resource,
  });
  const rawType = identity.authType || identity.type;
  const authTypeLabel = rawType
    ? rawType
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/^./, (c) => c.toUpperCase())
    : null;
  return {
    provider,
    authTypeLabel,
    value: identityDisplayValue(identity) ?? "Unknown identity",
    isPrimary: identity.isPrimary ?? false,
    mfaEnabled: identity.mfaEnabled ?? false,
  };
};

/* ------------------------------------------------------------------ *
 * Full-coverage adjustment (PLO-5461)
 *
 * Flagging every entitlement an account has is a full removal in all but
 * name. The server records such a submission as the removal outcome rather
 * than as an adjustment, so the dialog has to say so and gate it exactly as
 * the removal outcome is gated: that path starts de-provisioning immediately
 * and cannot be undone.
 * ------------------------------------------------------------------ */

/**
 * The outcome a fully-flagged adjustment is submitted as: the campaign's own
 * removal outcome. It is NOT always `not_required` — the compliance and
 * appropriateness campaign shapes offer no such option and use `non_compliant`
 * / `not_appropriate` instead, so a hardcoded mapping would leave the dialog
 * silent on exactly those campaigns.
 */
export const removalOutcomeOf = (
  outcomeOptions: readonly { name: string; standard_mapping: string }[],
): { name: string; standard_mapping: string } | null =>
  outcomeOptions.find((option) => isLockingOutcome(option.standard_mapping)) ??
  null;

/**
 * Has every entitlement of ONE account been flagged? An empty list is not "all
 * of them" — there is nothing to remove, so nothing is converted.
 */
export const everyEntitlementFlagged = (
  entitlementIds: readonly string[],
  flaggedIds: readonly string[],
): boolean => {
  if (entitlementIds.length === 0) return false;
  const flagged = new Set(flaggedIds);
  return entitlementIds.every((id) => flagged.has(id));
};

/**
 * How many of the selected accounts will be converted?
 *
 * This has to be asked per account, not against the deduplicated list the
 * dialog renders. Accounts on one resource SHARE entitlement ids, so a
 * selection that is plainly partial in the list can still be everything a
 * particular account holds: flagging one of two shown entitlements empties an
 * account that only ever had that one. The server converts per account, so a
 * check against the merged list would leave exactly those conversions
 * unannounced.
 */
export const accountsFullyFlaggedCount = (
  accountEntitlementIds: readonly (readonly string[])[],
  flaggedIds: readonly string[],
): number =>
  accountEntitlementIds.filter((ids) =>
    everyEntitlementFlagged(ids, flaggedIds),
  ).length;

/**
 * The outcome that will actually be recorded, which is not always the one the
 * reviewer clicked. Everything the dialog warns about keys off this.
 */
export const submittedOutcomeFor = (
  reviewAction: string | null,
  isAdjustment: boolean,
  emptiesAnAccount: boolean,
  outcomeOptions: readonly { name: string; standard_mapping: string }[],
): string | null => {
  if (!isAdjustment || !emptiesAnAccount) return reviewAction;
  // No removal outcome to convert to means the server leaves the submission
  // alone, so the dialog must not claim otherwise.
  return removalOutcomeOf(outcomeOptions)?.standard_mapping ?? reviewAction;
};

/** The campaign's own label for the removal outcome, when it offers one. */
export const removalOutcomeName = (
  outcomeOptions: readonly { name: string; standard_mapping: string }[],
): string | null => removalOutcomeOf(outcomeOptions)?.name ?? null;

export interface FullCoverageNotice {
  title: string;
  body: string;
}

/**
 * Copy for the callout shown once the last entitlement is flagged. It names
 * the campaign's own removal outcome where there is one, since that is the
 * label the reviewer will see on the row afterwards.
 *
 * `removal` is what the cycle does with the removal outcome this converts to
 * (`decisionRemovalTiming`). The callout used to promise immediate
 * de-provisioning unconditionally, which is untrue of a cycle that only lets
 * an admin remove the access at approval (PLO-5804). It defaults to `"now"` so
 * a caller that cannot say fails closed.
 */
/** What the conversion callout may say happens to the access, and when. */
const removalTail = (removal: DecisionRemoval): string => {
  switch (removal) {
    case "now":
      return ", and de-provisioning starts immediately";
    case "on_approval":
      return ", and an admin can remove the access when the review is approved";
    default:
      return "";
  }
};

export const fullCoverageNotice = (
  outcomeOptions: readonly { name: string; standard_mapping: string }[],
  convertingCount: number,
  selectedCount: number,
  removal: DecisionRemoval = "now",
): FullCoverageNotice => {
  const removalName = removalOutcomeName(outcomeOptions);
  const recordedAs = removalName
    ? `recorded as "${removalName}"`
    : "recorded as a full removal";

  // On a bulk decision the count matters: the reviewer needs to know that some
  // of the accounts they picked are being emptied even when the list in front
  // of them still shows unflagged entitlements belonging to the others.
  const subject =
    selectedCount > 1
      ? `every entitlement that ${convertingCount} of these ${selectedCount} accounts ${
          convertingCount === 1 ? "has" : "have"
        }, which leaves ${convertingCount === 1 ? "that account" : "those accounts"} with nothing`
      : "every entitlement this account has, which leaves it with nothing";

  const scope = selectedCount > 1 ? "their" : "its";

  return {
    title:
      selectedCount > 1 && convertingCount < selectedCount
        ? "Some of these accounts are being fully removed"
        : "Flagging everything is a full removal",
    body: `You have flagged ${subject}. This will be ${recordedAs} rather than an adjustment${removalTail(removal)}. Leave at least one of ${scope} entitlements unflagged to keep this an adjustment.`,
  };
};

/* ------------------------------------------------------------------ *
 * Entitlement adjustment scope (PLO-5460)
 *
 * "Adjust entitlements" only ever changes the entitlements the reviewer
 * flags; the account itself is left in place. Nothing on the outcome button
 * or in the confirmation dialog said so, so reviewers read it as a full
 * account removal. The dialog now spells the difference out, and points at
 * the removal outcome for reviewers who did mean to remove the account.
 * ------------------------------------------------------------------ */

/**
 * The least an outcome has to carry to be recognised. The row/bulk action
 * layers hold options without a `name`, and they ask the same question.
 */
export interface OutcomeMappingLike {
  standard_mapping: string;
  action_type?: string;
  /** carried by most callers; never read here. */
  name?: string;
}

/** The subset of a campaign outcome option this module needs: a recognisable
 * outcome that also names itself. */
export interface OutcomeOptionLike extends OutcomeMappingLike {
  name: string;
}

/**
 * Does this outcome open the entitlement-adjustment flow? Campaigns configure
 * it either as an `adjust_entitlements` action on a custom-named outcome or as
 * the standard mapping itself, so both spellings count.
 */
export const isEntitlementAdjustmentOutcome = (
  option: OutcomeMappingLike | null | undefined,
): boolean =>
  option?.action_type === "adjust_entitlements" ||
  option?.standard_mapping === "adjust_entitlements";

/** An account's current existing-entitlement ids (the snapshot ids). */
export const currentEntitlementIds = (a: {
  entitlements?: Array<{ snapshotEntitlementId?: string | null }> | null;
}): string[] =>
  (a.entitlements || [])
    .map((e) => e.snapshotEntitlementId)
    .filter((id): id is string => !!id);

/** A group the adjust picker offers in place of the entitlements it grants. */
export interface GrantingGroup {
  id: string;
  name: string;
  entitlementIds: string[];
  entitlementNames: string[];
}

type GroupableAccount = {
  accessKind?: MFAccessKind | null;
  accessPaths?: MFAccessPath[] | null;
  entitlements?: Array<{
    snapshotEntitlementId?: string | null;
    snapshotEntitlementName?: string | null;
    snapshotEntitlementType?: string | null;
  }> | null;
};

const entitlementKey = (name?: string | null, type?: string | null) =>
  `${name ?? ""}\u0000${type ?? ""}`;

/**
 * The groups an indirect selection's access is granted through — each route's
 * first hop, the resource the person is removed from (PLO-6260). Access held
 * through a group is granted and taken away at the group, so the adjust picker
 * ticks each group's entitlements together. A group carries the snapshot ids
 * of every entitlement its routes grant: the snapshot is built from all hops,
 * unique by name and type, so it is matched back the same way. Its names are
 * only the last hop's — what it grants on the reviewed resource.
 *
 * The flag set is shared across the whole selection, so it can only speak for
 * every account at once when they all hold the access through the same groups
 * granting the same things. Otherwise — direct accounts in the mix, or indirect
 * ones held differently — ticking one group would flag ids on accounts outside
 * it and could empty them into a removal nobody picked, so nothing groups and
 * `separate` tells the picker to block the adjustment.
 */
export const grantingGroups = (
  accounts: GroupableAccount[],
): { groups: GrantingGroup[]; separate: boolean } => {
  const indirect = accounts.filter(isIndirectAccount);
  if (indirect.length === 0) return { groups: [], separate: false };
  if (indirect.length < accounts.length) return { groups: [], separate: true };

  const [first, ...rest] = indirect.map(accountGroups);
  const signature = (groups: GrantingGroup[]) =>
    JSON.stringify(
      groups
        .map((g) => [g.id, [...g.entitlementIds].sort()] as const)
        .sort(([a], [b]) => a.localeCompare(b)),
    );
  if (rest.some((groups) => signature(groups) !== signature(first))) {
    return { groups: [], separate: true };
  }
  return { groups: first, separate: false };
};

/** One indirect account's granting groups — see `grantingGroups`. */
const accountGroups = (account: GroupableAccount): GrantingGroup[] => {
  const snapshotIds = new Map<string, string>();
  (account.entitlements || []).forEach((e) => {
    if (e.snapshotEntitlementId) {
      snapshotIds.set(
        entitlementKey(e.snapshotEntitlementName, e.snapshotEntitlementType),
        e.snapshotEntitlementId,
      );
    }
  });

  const groups = new Map<
    string,
    { name: string; ids: Set<string>; names: Set<string> }
  >();
  (account.accessPaths || []).forEach(({ hops }) => {
    const [head] = hops;
    if (!head) return;
    const group = groups.get(head.resourceId) ?? {
      name: hopLabel(head),
      ids: new Set<string>(),
      names: new Set<string>(),
    };
    hops.forEach((hop, index) =>
      (hop.entitlements ?? []).forEach((e) => {
        const id = snapshotIds.get(entitlementKey(e.name, e.type));
        if (!id) return;
        group.ids.add(id);
        if (index === hops.length - 1) group.names.add(e.name);
      }),
    );
    groups.set(head.resourceId, group);
  });

  return Array.from(groups, ([id, group]) => ({
    id,
    name: group.name,
    entitlementIds: [...group.ids],
    entitlementNames: [...group.names],
  }));
};

/**
 * The entitlements to flag when the reviewer has ticked `ticked` groups — or
 * picker units, see `pickerUnits`: what the person actually loses. An
 * entitlement another, unticked group also grants stays — the snapshot holds
 * one id per name, so a group can share every id with another and removing it
 * alone flags nothing. Ticking every one is a removal of the access, so it
 * flags `allIds`, which is what turns the decision into the removal outcome
 * (PLO-5461).
 */
export const flagsForGroups = (
  ticked: ReadonlySet<string>,
  groups: GrantingGroup[],
  allIds: string[],
): string[] => {
  if (groups.length > 0 && groups.every((g) => ticked.has(g.id))) return allIds;
  const kept = new Set(
    groups.filter((g) => !ticked.has(g.id)).flatMap((g) => g.entitlementIds),
  );
  return [
    ...new Set(
      groups
        .filter((g) => ticked.has(g.id))
        .flatMap((g) => g.entitlementIds)
        .filter((id) => !kept.has(id)),
    ),
  ];
};

/** A line of the group-held adjust picker — see `pickerUnits`. */
export interface PickerUnit extends GrantingGroup {
  together: boolean;
}

/**
 * What the group-held adjust picker lists (PLO-6260): any entitlement no group
 * accounts for on its own, then each granting group as a block of its
 * entitlements, ticked together. The ungrouped ones are single-entitlement units so
 * `flagsForGroups` still reads "every unit ticked" as a removal; their ids are
 * prefixed so they cannot collide with a group's. A group whose last hop grants
 * nothing named lists the entitlements behind its ids instead, so its block is
 * never empty.
 */
export const pickerUnits = (
  groups: GrantingGroup[],
  entitlements: Array<{
    snapshotEntitlementId: string;
    snapshotEntitlementName: string;
  }>,
): PickerUnit[] => {
  const names = new Map(
    entitlements.map((e) => [
      e.snapshotEntitlementId,
      e.snapshotEntitlementName,
    ]),
  );
  const grouped = new Set(groups.flatMap((g) => g.entitlementIds));
  return [
    ...entitlements
      .filter((e) => !grouped.has(e.snapshotEntitlementId))
      .map((e) => ({
        id: `entitlement:${e.snapshotEntitlementId}`,
        name: e.snapshotEntitlementName,
        entitlementIds: [e.snapshotEntitlementId],
        entitlementNames: [e.snapshotEntitlementName],
        together: false,
      })),
    ...groups.map((g) => ({
      ...g,
      entitlementNames:
        g.entitlementNames.length > 0
          ? g.entitlementNames
          : g.entitlementIds.flatMap((id) => names.get(id) ?? []),
      together: true,
    })),
  ];
};

export interface AdjustmentScopeNotice {
  title: string;
  body: string;
}

/**
 * Copy for the "this is not a full removal" callout in the confirmation
 * dialog. The removal outcome is named from the campaign's own options when it
 * offers one, since its label is configurable per campaign and a reviewer
 * looking for it needs the name they can actually see.
 */
export const adjustmentScopeNotice = (
  outcomeOptions: readonly OutcomeOptionLike[],
  selectedCount: number,
): AdjustmentScopeNotice => {
  const subject =
    selectedCount > 1
      ? "The accounts stay in place"
      : "The account stays in place";

  // Any of the removal mappings, not just "not_required": the compliance and
  // appropriateness campaign shapes use `non_compliant` / `not_appropriate` as
  // their removal outcome and offer no `not_required` at all, so keying on that
  // one name would send those reviewers the generic fallback.
  const removalName = outcomeOptions.find((option) =>
    isLockingOutcome(option.standard_mapping),
  )?.name;

  const alternative = removalName
    ? `choose "${removalName}" instead`
    : "choose the full removal outcome instead";

  return {
    title: "This is an adjustment, not a full account removal",
    body: `Only the entitlements you flag are sent for adjustment. ${subject}, along with every entitlement you leave unflagged. To remove the whole account, cancel and ${alternative}.`,
  };
};

export type ReviewView = "entitlements" | "recommendations";

/**
 * Which view a review opens on. An explicit `?view=` (or legacy `?tab=`) deep
 * link wins; otherwise the org's default (PLO-6504). The org setting says
 * "suggestions"; the URL value stays "recommendations" for existing links.
 */
export const resolveReviewView = ({
  requested,
  orgDefault,
}: {
  requested: string | null;
  orgDefault: MFMember["accessReviewDefaultView"];
}): ReviewView => {
  const view =
    requested ?? (orgDefault === "suggestions" ? "recommendations" : null);
  return view === "recommendations" ? "recommendations" : "entitlements";
};
